import type Stripe from "stripe";

import {
  type AnyPgColumn,
  bigint,
  boolean,
  check,
  date,
  doublePrecision,
  index,
  integer,
  jsonb,
  pgEnum,
  pgSequence,
  pgTable,
  primaryKey,
  smallint,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
  vector,
} from "drizzle-orm/pg-core";
import {
  libraryShelfSchema,
  practiceStageBandSchema,
  prcprLinkSchema,
  seriesFamilySchema,
} from "~/lib/shared/content-status";
import type { JsonValue } from "~/lib/shared/json";
import type { TIconName } from "~/lib/icon-names";
import type { SummitSessionFormat } from "~/lib/summit";
import type { NarrativeStat } from "~/lib/shared/cih/narrative-stats";
import type { NovaTestScenarioDescriptor } from "~/lib/shared/nova/test-scenarios";
import type { NovaTurnEffect } from "~/lib/shared/nova/turn-effects";
import type { FreeAccessProduct } from "~/lib/shared/nova/free-access";
import type { PlanKey } from "~/lib/shared/plan-catalogue";
import type {
  ProgramTemplateHomework,
  ProgramTemplateInstalment,
} from "~/lib/schemas/program-template";
import type {
  CourseMaterialKind,
  CourseStatus,
} from "~/lib/shared/learning/courses";
import type { VideoAudience } from "~/lib/shared/learning/placements";
import type { AssetKey } from "~/lib/shared/nova/assets/catalog";
import type { WhatsComingGroup } from "~/lib/shared/features/groups";
import type { FeatureModule } from "~/lib/shared/features/modules";
import type { FeatureStoredStage } from "~/lib/shared/features/stage";
import {
  NOVA_ROUTINE_RUN_STATUSES,
  NOVA_ROUTINE_STATUSES,
  type NovaRoutineKind,
} from "~/lib/shared/nova/routines";
import type {
  ReadinessAnswers,
  ReadinessResult,
} from "~/lib/shared/readiness/engine";
import type { UpgradePromptKey } from "~/lib/shared/upgrade-prompts";
import { sql } from "drizzle-orm";
import {
  MembershipPlan,
  CoachEmbeddingType,
  AppointmentStatus,
  ExpertiseType,
  CCOType,
  CcoAccessRequestStatus,
  PageChangeActorRole,
  PageChangeEvent,
  PageChangePageType,
  ClaimVerificationState,
  OfferStatus,
  OfferType,
  RegistrationStatus,
  EngagementStatus,
  SessionStatus,
  TaskStatus,
  NotificationType,
  CalendarProviderName,
  CalendarConnectionStatus,
  CalendarEventSubjectType,
  CalendarEventSyncState,
  ClientCalendarSource,
  MeetingProvider,
} from "~/lib/schemas/db-enums";
import {
  ContentStatus as ContentStatusEnum,
  FrameworkType as FrameworkTypeEnum,
  GuideType as GuideTypeEnum,
  PracticeStage as PracticeStageEnum,
  IpSensitivity as IpSensitivityEnum,
  PartnerTier as PartnerTierEnum,
} from "~/lib/schemas/db-enums-cih";

// Re-export enums for backwards compatibility
export { EngagementStatus, SessionStatus, TaskStatus, NotificationType };

/** How far ahead a Life Design goal looks. */
export enum GoalTimeline {
  QUARTER = "quarter",
  YEAR = "year",
  THREE_YEARS = "three_years",
}

/** Progress of any goal: a Life Design goal, a step under it, or a program goal. */
export enum GoalStatus {
  PLANNED = "planned",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
}

export function enumToPgEnum<T extends { [key: string]: string }>(
  myEnum: T,
): [T[keyof T], ...T[keyof T][]] {
  // Capture enum values directly as string literals
  return Object.values(myEnum) as [T[keyof T], ...T[keyof T][]];
}

export const membershipPlanEnum = pgEnum(
  "membership_plan",
  enumToPgEnum(MembershipPlan),
);

export const coachEmbeddingTypeEnum = pgEnum(
  "coach_embedding_type",
  enumToPgEnum(CoachEmbeddingType),
);

export const appointmentStatusEnum = pgEnum(
  "appointment_status",
  enumToPgEnum(AppointmentStatus),
);

export const expertiseTypeEnum = pgEnum(
  "expertise_type",
  enumToPgEnum(ExpertiseType),
);

export const ccoTypeEnum = pgEnum("cco_type", enumToPgEnum(CCOType));

export const ccoAccessRequestStatusEnum = pgEnum(
  "cco_access_request_status",
  enumToPgEnum(CcoAccessRequestStatus),
);

export const pageChangePageTypeEnum = pgEnum(
  "page_change_page_type",
  enumToPgEnum(PageChangePageType),
);

export const pageChangeEventEnum = pgEnum(
  "page_change_event",
  enumToPgEnum(PageChangeEvent),
);

export const pageChangeActorRoleEnum = pgEnum(
  "page_change_actor_role",
  enumToPgEnum(PageChangeActorRole),
);

export const claimVerificationStateEnum = pgEnum(
  "claim_verification_state",
  enumToPgEnum(ClaimVerificationState),
);

export const offerTypeEnum = pgEnum("offer_type", enumToPgEnum(OfferType));

export const offerStatusEnum = pgEnum(
  "offer_status",
  enumToPgEnum(OfferStatus),
);

export const registrationStatusEnum = pgEnum(
  "registration_status",
  enumToPgEnum(RegistrationStatus),
);

export const engagementStatusEnum = pgEnum(
  "engagement_status",
  enumToPgEnum(EngagementStatus),
);

export const sessionStatusEnum = pgEnum(
  "session_status",
  enumToPgEnum(SessionStatus),
);

export const meetingProviderEnum = pgEnum(
  "meeting_provider",
  enumToPgEnum(MeetingProvider),
);

export const clientCalendarSourceEnum = pgEnum(
  "client_calendar_source",
  enumToPgEnum(ClientCalendarSource),
);

export const taskStatusEnum = pgEnum("task_status", enumToPgEnum(TaskStatus));

export const notificationTypeEnum = pgEnum(
  "notification_type",
  enumToPgEnum(NotificationType),
);

export const goalTimelineEnum = pgEnum(
  "goal_timeline",
  enumToPgEnum(GoalTimeline),
);

export const goalStatusEnum = pgEnum("goal_status", enumToPgEnum(GoalStatus));

export const contentStatusEnum = pgEnum(
  "content_status",
  enumToPgEnum(ContentStatusEnum),
);

export const frameworkTypeEnum = pgEnum(
  "framework_type",
  enumToPgEnum(FrameworkTypeEnum),
);

export const guideTypeEnum = pgEnum("guide_type", enumToPgEnum(GuideTypeEnum));

export const practiceStageEnum = pgEnum(
  "practice_stage",
  enumToPgEnum(PracticeStageEnum),
);

// The PrcPR taxonomy enums come straight from their Zod schemas rather than a
// TypeScript enum: the column type is then the same plain string union the
// parsers produce, so values flow into these columns without a cast.
export const practiceStageBandEnum = pgEnum(
  "practice_stage_band",
  practiceStageBandSchema.options,
);

export const prcprLinkEnum = pgEnum("prcpr_link", prcprLinkSchema.options);

// Same reasoning as the two enums above: these come from Zod schemas rather
// than TypeScript enums so parsed sheet strings flow into the columns without
// a cast.
export const libraryShelfEnum = pgEnum(
  "library_shelf",
  libraryShelfSchema.options,
);

export const seriesFamilyEnum = pgEnum(
  "series_family",
  seriesFamilySchema.options,
);

export const ipSensitivityEnum = pgEnum(
  "ip_sensitivity",
  enumToPgEnum(IpSensitivityEnum),
);

export const partnerTierEnum = pgEnum(
  "partner_tier",
  enumToPgEnum(PartnerTierEnum),
);

// Coach referral reward lifecycle: pending → granting → granted, with
// clawed_back reachable from the first three within the clawback window.
export const coachReferralRewardStatusEnum = pgEnum(
  "coach_referral_reward_status",
  ["pending", "granting", "granted", "clawed_back"],
);

const timestamps = {
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" })
    .defaultNow()
    .$onUpdate(() => new Date())
    .notNull(),
} as const;

const contentStatusColumns = {
  status: contentStatusEnum("status")
    .notNull()
    .default(ContentStatusEnum.Draft),
  authorId: text("author_id"),
  reviewedBy: text("reviewed_by"),
  reviewNote: text("review_note"),
  submittedAt: timestamp("submitted_at", { mode: "date" }),
  publishedAt: timestamp("published_at", { mode: "date" }),
  archivedAt: timestamp("archived_at", { mode: "date" }),
  version: integer("version").notNull().default(1),
} as const;

export const coaches = pgTable(
  "Coach",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    username: text("username").notNull().unique(),

    firstName: text("first_name").notNull(),
    lastName: text("last_name").notNull(),
    fullName: text("full_name").notNull(),

    email: text("email").notNull().unique(),
    phone: text("phone"),

    profilePictureUrl: text("profile_picture_url"),

    clerkUserId: text("clerk_user_id").unique(),
    userId: uuid("user_id")
      .references((): AnyPgColumn => users.id, {
        onDelete: "set null",
      })
      .unique(),

    ghlContactId: text("ghl_contact_id").unique(),
    ghlLocationId: text("ghl_location_id").unique(),
    ghlCalendarId: text("ghl_calendar_id"),
    ghlUserId: text("ghl_user_id"),

    profileId: uuid("profile_id"),
    mainOfferId: uuid("main_offer_id"),
    coachDetailsId: uuid("coach_details_id"),

    source: text("source"),
    // Onboarding-funnel entry lane: "self_serve" | "program". Written at
    // registration from the entry cookie's program marker; read by checkout
    // (trial eligibility) and the funnel event lane fallback.
    acquisitionLane: text("acquisition_lane"),

    membershipPlan: membershipPlanEnum("membership_plan")
      .notNull()
      .default(MembershipPlan.Standard),
    stripeCustomerId: text("stripe_customer_id").unique(),
    stripeSubscriptionId: text("stripe_subscription_id").unique(),
    subscriptionStatus: text("subscription_status"),

    novaSubscriptionId: text("nova_subscription_id").unique(),
    novaSubscriptionStatus: text("nova_subscription_status"),
    novaSubscriptionPeriodEnd: timestamp("nova_subscription_period_end", {
      mode: "date",
    }),
    novaSubscriptionCancelAtPeriodEnd: boolean(
      "nova_subscription_cancel_at_period_end",
    ),
    // Stripe's `canceled_at` for the tracked subscription while
    // `cancel_at_period_end` is true, null otherwise. Stripe sets it anew on
    // every cancel request, so it tells a cancel we already recorded from a
    // cancel that was undone and requested again, and it is the occurrence key
    // of the cancel and undo funnel events. Null with the flag true is a row
    // written before this column, whose cancel was recorded under the old key.
    novaSubscriptionCanceledAt: timestamp("nova_subscription_canceled_at", {
      mode: "date",
    }),
    // Which plan the tracked Nova subscription bills — a `PlanKey`, resolved
    // from the subscription's product (`product_kind`, falling back to the
    // price's lookup key) by `novaSubscriptionColumnsFromStripe`. Written and
    // cleared with the four columns above, by the same guarded UPDATE, so it
    // can never describe a subscription the row no longer tracks.
    //
    // Plain text rather than an enum: the catalogue is a code constant that ops
    // can add to without a migration, and an unrecognised product stores null
    // rather than failing a webhook. Null means "not resolved" — either a
    // subscriber whose last event predates this column, or a product naming no
    // plan of ours.
    novaPlanKey: text("nova_plan_key").$type<PlanKey>(),
    // Set once, the first time a Nova subscription reports `trialing` (webhook),
    // and never cleared. The durable record of trial consumption — the other Nova
    // columns are wiped on cancel, so this column is what makes trials one-per-coach.
    novaTrialStartedAt: timestamp("nova_trial_started_at", { mode: "date" }),
    // Set once, the first time a Nova subscription is observed `active`, and
    // never cleared. `novaTrialStartedAt` cannot stand in for this: it is
    // stamped only for `trialing`, so bundle buyers, Stripe payment-link
    // buyers and direct subscribers never receive it, and after cancellation
    // (which wipes the other Nova columns) they read as never-subscribed and
    // are offered a fresh 7-day trial. Trial eligibility is lifetime, so this
    // column is what sends a returning customer straight to paid.
    novaPaidStartedAt: timestamp("nova_paid_started_at", { mode: "date" }),
    // Set once, from Stripe's `trial_start`, when a subscription made by the
    // win-back checkout is first observed, and never cleared. A lapsed coach
    // gets one free 7-day win-back trial; this column is what makes it one.
    // `novaTrialStartedAt` cannot stand in for it: it records the FIRST trial
    // ever, which every lapsed trialist already carries.
    novaWinbackTrialStartedAt: timestamp("nova_winback_trial_started_at", {
      mode: "date",
    }),
    // Incremented when a NOVA sync plans a Stripe read. Unlike the timestamp
    // watermark below, this distinguishes reads that start in the same
    // millisecond and lets the newest planned read win under the row lock.
    novaSubscriptionObservationVersion: integer(
      "nova_subscription_observation_version",
    )
      .notNull()
      .default(0),
    // Highest version that completed a Stripe observation and applied. A
    // failed newer read must not block an older successful read.
    novaSubscriptionAppliedObservationVersion: integer(
      "nova_subscription_applied_observation_version",
    )
      .notNull()
      .default(0),
    // When the last applied Stripe read started (`observedAt` in
    // `applyNovaObservation`). A read that started earlier than this is older
    // than what the row already holds, so it writes nothing and records no
    // event. Null means nothing has been applied yet, and any read wins.
    novaSubscriptionEventAt: timestamp("nova_subscription_event_at", {
      mode: "date",
    }),
    // Last time `rescueWalledNovaCoach` asked Stripe whether this coach already
    // paid for Nova outside checkout. With enforcement on, "walled" is every
    // coach without a live subscription, so this throttle is what keeps an
    // uncached Stripe list/search off the Nova open path.
    novaRescueCheckedAt: timestamp("nova_rescue_checked_at", { mode: "date" }),
    // Last time a page read or the reconcile cron claimed a Stripe read for
    // this coach's NOVA subscription, successful or not. The claim is one
    // conditional update, so parallel loaders and several tabs cost one Stripe
    // read per interval (`refreshNovaSubscriptionIfStale`).
    novaStripeCheckedAt: timestamp("nova_stripe_checked_at", { mode: "date" }),
    // Free NOVA Core from Dream Coach Match: a copy of her open
    // `CoachFreeAccessGrant`, so access stays a read of this row. When the
    // grant started, and when it stops (null: until staff end it). Written only
    // by `~/lib/server/nova/free-access`, in the grant's own transaction.
    novaFreeAccessFrom: timestamp("nova_free_access_from", { mode: "date" }),
    novaFreeAccessUntil: timestamp("nova_free_access_until", { mode: "date" }),

    stripeAccountId: text("stripe_account_id"),
    stripeChargesEnabled: boolean("stripe_charges_enabled"),
    stripePayoutsEnabled: boolean("stripe_payouts_enabled"),
    // track manual profile edits and onboarding completion
    profile_manually_edited: boolean("profile_manually_edited")
      .notNull()
      .default(false),
    onboarding_completed_at: timestamp("onboarding_completed_at", {
      mode: "date",
    }),
    contractSigned: boolean("contract_signed").default(false),
    isFoundingCoach: boolean("is_founding_coach").default(false),
    isFeaturedCoach: boolean("is_featured_coach").default(false),

    referralCode: text("referral_code"),
    referredByCoachId: uuid("referred_by_coach_id").references(
      (): AnyPgColumn => coaches.id,
    ),
    // When the referral attribution was stamped. Nullable with no default —
    // forward-only: pre-launch coaches keep a null value and earn no reward.
    referredAt: timestamp("referred_at", { mode: "date" }),
    // The organization whose invite link the coach joined through, and when.
    // Credit only, with no reward; written once at registration.
    invitedByCcoId: uuid("invited_by_cco_id").references(
      (): AnyPgColumn => ccos.id,
      { onDelete: "set null" },
    ),
    invitedAt: timestamp("invited_at", { mode: "date" }),

    featureResources: boolean("feature_resources"),

    isLifetimeMember: boolean("is_lifetime_member").default(false),
    lifetimePurchaseDate: timestamp("lifetime_purchase_date", { mode: "date" }),

    approvedAt: timestamp("approved_at", { mode: "date" }),
    approvedBy: text("approved_by"),
    // Explicit revocation marker for tier-1 (marketplace-listing) approval —
    // distinguishes "revoked" from "never approved" (both look like null
    // approvedAt otherwise). Automatic approval callers no-op while this is
    // set; only an explicit human approval action clears it.
    tier1RevokedAt: timestamp("tier1_revoked_at", { mode: "date" }),
    tier1RevokedBy: text("tier1_revoked_by"),
    marketplaceApprovedAt: timestamp("marketplace_approved_at", {
      mode: "date",
    }),
    marketplaceApprovedBy: text("marketplace_approved_by"),
    marketplaceApprovalRequestedAt: timestamp(
      "marketplace_approval_requested_at",
      { mode: "date" },
    ),
    marketplaceApprovalReturnedAt: timestamp(
      "marketplace_approval_returned_at",
      { mode: "date" },
    ),
    marketplaceApprovalRevokedAt: timestamp("marketplace_approval_revoked_at", {
      mode: "date",
    }),

    // Business Builder Accelerator. Independent of the NOVA columns above: she
    // keeps the Accelerator when NOVA lapses. The purchase pair is written by
    // the Stripe checkout and cleared by an admin after a refund.
    acceleratorPurchasedAt: timestamp("accelerator_purchased_at", {
      mode: "date",
    }),
    acceleratorCheckoutSessionId: text(
      "accelerator_checkout_session_id",
    ).unique(),
    // Set by an admin when she joins a cohort, or by the graduate import.
    // Cohorts 1 and 2 keep a NOVA Pro+ discount (`ACCELERATOR_DISCOUNT_COHORTS`).
    acceleratorCohort: smallint("accelerator_cohort"),
    // The graduation date, stored as UTC midnight of that calendar day.
    acceleratorGraduatedAt: timestamp("accelerator_graduated_at", {
      mode: "date",
    }),
    // The admin's email, or the import script, that set the graduation date.
    acceleratorGraduatedBy: text("accelerator_graduated_by"),

    ...timestamps,
  },
  (table) => [
    // Referral attribution lookups (the referrer's referred-headcount aggregate
    // in `getReferralCreditSummary`). Postgres does not auto-index FK columns,
    // so without this every read full-scans Coach.
    index("coach_referred_by_coach_id_idx").on(table.referredByCoachId),
    // The invite count on an organization's "Invite graduates" page.
    index("coach_invited_by_cco_id_idx").on(table.invitedByCcoId),
    check(
      "coach_accelerator_cohort_positive",
      sql`${table.acceleratorCohort} IS NULL OR ${table.acceleratorCohort} > 0`,
    ),
    // A graduate always belongs to a cohort; the cohort decides her discount.
    check(
      "coach_accelerator_graduate_has_cohort",
      sql`${table.acceleratorGraduatedAt} IS NULL OR ${table.acceleratorCohort} IS NOT NULL`,
    ),
    check(
      "coach_nova_free_access_until_has_from",
      sql`${table.novaFreeAccessUntil} IS NULL OR ${table.novaFreeAccessFrom} IS NOT NULL`,
    ),
  ],
);

export type CoachInsert = typeof coaches.$inferInsert;
export type CoachSelect = typeof coaches.$inferSelect;

/**
 * Free access to a paid product, given by staff, and its history. A grant is
 * open until `endedAt` is set; at most one is open per coach and product.
 * `endsAt` is when access stops by itself (null: until staff end it), and a
 * grant that ran out stays open until the next give or end closes it with
 * `endedAt = endsAt` and no ender. The open grant is copied onto
 * `Coach.novaFreeAccessFrom`/`novaFreeAccessUntil`.
 */
export const coachFreeAccessGrants = pgTable(
  "CoachFreeAccessGrant",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    product: text("product").$type<FreeAccessProduct>().notNull(),
    givenAt: timestamp("given_at", { mode: "date" }).notNull().defaultNow(),
    givenByUserId: uuid("given_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    endsAt: timestamp("ends_at", { mode: "date" }),
    reason: text("reason").notNull(),
    endedAt: timestamp("ended_at", { mode: "date" }),
    endedByUserId: uuid("ended_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (table) => [
    index("coach_free_access_grant_coach_id_idx").on(table.coachId),
    uniqueIndex("coach_free_access_grant_open_unique")
      .on(table.coachId, table.product)
      .where(sql`${table.endedAt} IS NULL`),
    check("coach_free_access_grant_product", sql`${table.product} = 'nova'`),
    check(
      "coach_free_access_grant_reason_present",
      sql`btrim(${table.reason}) <> ''`,
    ),
  ],
);

export type CoachFreeAccessGrantSelect =
  typeof coachFreeAccessGrants.$inferSelect;

export const coachStatusRegistrations = pgTable(
  "CoachStatusRegistrations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ghlContactId: text("ghl_contact_id"),
    email: text("email"),
    isFoundingCoach: boolean("is_founding_coach").notNull().default(false),
    isFeaturedCoach: boolean("is_featured_coach").notNull().default(false),
    notes: text("notes"),
    pendingApprovalAction: text("pending_approval_action"),
    ...timestamps,
  },
  (table) => ({
    contactUnique: uniqueIndex("coach_status_registrations_contact_unique").on(
      table.ghlContactId,
    ),
    emailUnique: uniqueIndex("coach_status_registrations_email_unique").on(
      table.email,
    ),
  }),
);

export type CoachStatusRegistrationInsert =
  typeof coachStatusRegistrations.$inferInsert;
export type CoachStatusRegistrationSelect =
  typeof coachStatusRegistrations.$inferSelect;

export const offers = pgTable("Offer", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  type: offerTypeEnum("type").notNull().default(OfferType.Standard),
  // Only published offers are shown publicly or purchasable. Transitions go
  // through `~/lib/server/offer-lifecycle`, never through a plain update.
  status: offerStatusEnum("status").notNull().default(OfferStatus.Draft),
  // Stamped on every transition to published; orders "most recently
  // published" when the main offer is reassigned. Null for offers that were
  // already public before the lifecycle existed.
  publishedAt: timestamp("published_at", { mode: "date" }),
  platformFeeBps: integer("platform_fee_bps"),
  summitId: uuid("summit_id").references(() => summits.id, {
    onDelete: "set null",
  }),
  summitSessionId: uuid("summit_session_id")
    .references(() => summitSessions.id, { onDelete: "set null" })
    .unique(),

  // Main headlines and descriptions
  preHeadline: text("pre_headline"),
  headline: text("headline"),
  description: text("description"),

  // Direct sale configuration
  directSaleEnabled: boolean("direct_sale_enabled").default(false),
  price: integer("price"), // stored in cents
  currency: text("currency").default("USD"),
  originalPrice: integer("original_price"), // stored in cents, for strikethrough price

  // Struggles
  strugglesHeadline: text("struggles_headline"),
  strugglesDescription: text("struggles_description"),
  strugglesImageUrl: text("struggles_image_url"),

  // Results section
  resultsPreHeadline: text("results_pre_header").default(
    "Results you can expect",
  ),
  resultsHeadline: text("results_headline"),
  resultsDescription: text("results_description"),
  resultsImageUrl: text("results_image_url"),

  // Program details
  programHeadline: text("program_headline"),
  programDescription: text("program_description"),
  programImageUrl: text("program_image_url"),

  // Bottom CTA
  bottomCtaHeadline: text("bottom_cta_headline"),
  bottomCtaText: text("bottom_cta_text"),

  // Optional override for the CTA button link (e.g. a coach's own Calendly /
  // Cal.com URL). When set, replaces the default /:username/book or /:username/contact
  // destination for the apply-mode CTA on the offer page.
  ctaUrl: text("cta_url"),

  // Dates
  applicationDeadline: timestamp("application_deadline"),
  cohortStartDate: timestamp("cohort_start_date"),

  // Media
  vslVideoUrl: text("vsl_video_url"),

  // Mini Sprint — curriculum unit label ("Day" | "Week" | "Module")
  durationLabel: text("duration_label"),

  // Mini Sprint — coach-editable hero eyebrow/badge (e.g. "FREE 5-DAY MINI
  // SPRINT"). When null, the page auto-generates it from durationLabel + the
  // number of curriculum items.
  heroEyebrow: text("hero_eyebrow"),

  // Mini Sprint — the named artifact the client keeps, rendered as the
  // "What you walk away with" callout. One short page-ready line; null
  // for non-sprint offers.
  sprintTakeaway: text("sprint_takeaway"),

  // Guarantee
  satisfactionGuaranteeText: text("satisfaction_guarantee_text"),
});

export type OfferInsert = typeof offers.$inferInsert;
export type OfferSelect = typeof offers.$inferSelect;
/** Offer fields a content write may set. The lifecycle columns change only
 * through `~/lib/server/offer-lifecycle`, so an ordinary insert or update
 * cannot publish an offer. */
export type OfferContentInsert = Omit<OfferInsert, "status" | "publishedAt">;

export const offerBenefits = pgTable("OfferBenefit", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  content: text("content"),
  imageSrc: text("image_src"),
  order: integer("order").notNull().default(0),
});

export type OfferBenefitInsert = typeof offerBenefits.$inferInsert;
export type OfferBenefitSelect = typeof offerBenefits.$inferSelect;

export const offerComponents = pgTable("OfferComponent", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),

  preHeader: text("pre_header"),
  title: text("title").notNull(),
  content: text("content").notNull(),
  imageUrl: text("image_url"),
  order: integer("order").notNull().default(0),
});

export type OfferComponentInsert = typeof offerComponents.$inferInsert;
export type OfferComponentSelect = typeof offerComponents.$inferSelect;

export const offerTags = pgTable("OfferTag", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  property: text("property").notNull(),
  order: integer("order").notNull().default(0),
});

export type OfferTagInsert = typeof offerTags.$inferInsert;
export type OfferTagSelect = typeof offerTags.$inferSelect;

export const offerBonuses = pgTable("OfferBonus", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  content: text("content").notNull(),
  imageSrc: text("image_src"),
  order: integer("order").notNull().default(0),
});

export type OfferBonusInsert = typeof offerBonuses.$inferInsert;
export type OfferBonusSelect = typeof offerBonuses.$inferSelect;

export const offerFaqs = pgTable("OfferFaq", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),
  question: text("question").notNull(),
  answer: text("answer").notNull(),
  order: integer("order").notNull().default(0),
});

export type OfferFaqInsert = typeof offerFaqs.$inferInsert;
export type OfferFaqSelect = typeof offerFaqs.$inferSelect;

export const offerCurriculum = pgTable("OfferCurriculumItem", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  content: text("content").notNull(), // stringified Tiptap JSON
  order: integer("order").notNull().default(0),
});

export type OfferCurriculumItemInsert = typeof offerCurriculum.$inferInsert;
export type OfferCurriculumItemSelect = typeof offerCurriculum.$inferSelect;

export const testimonials = pgTable(
  "Testimonial",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),

    content: text("content").notNull(),
    author: text("author").notNull(),
    order: integer("order").notNull().default(0),

    // Engagement-verified client testimonials ("client" source). Coach-typed
    // profile testimonials remain the "coach" source with no rating/links.
    source: text("source", { enum: ["coach", "client"] })
      .notNull()
      .default("coach"),
    // Coach controls display but can never edit client-authored content
    status: text("status", { enum: ["pending", "published", "hidden"] })
      .notNull()
      .default("published"),
    engagementId: uuid("engagement_id").references(() => engagements.id, {
      onDelete: "set null",
    }),
    userId: uuid("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    rating: integer("rating"),
    submittedAt: timestamp("submitted_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    engagementUnique: uniqueIndex("testimonial_engagement_id_unique")
      .on(table.engagementId)
      .where(sql`engagement_id IS NOT NULL`),
    coachIdIdx: index("testimonial_coach_id_idx").on(table.coachId),
  }),
);

export type TestimonialInsert = typeof testimonials.$inferInsert;
export type TestimonialSelect = typeof testimonials.$inferSelect;

export const profiles = pgTable(
  "Profile",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" })
      .unique(),

    isPublic: boolean("is_public").notNull().default(false),
    isPreGeneratedProfile: boolean("is_pre_generated_profile")
      .notNull()
      .default(false),
    searchIndexingAllowed: boolean("search_indexing_allowed")
      .notNull()
      .default(true),

    backgroundInformation: text("background_information"),

    brandName: text("brand_name"),
    brandLogoUrl: text("brand_logo_url"),

    coachingTitle: text("coaching_title"),
    languages: text("languages"),
    formOfConduct: text("coaching_level"),
    coachingStyle: text("coaching_style"),
    audienceFor: text("audience_for"),
    typicalEngagement: text("typical_engagement"),
    coachingSinceYear: integer("coaching_since_year"),
    primaryCity: text("primary_city"),
    primaryCountry: text("primary_country"),
    closestMajorCity: text("closest_major_city"),

    heroImageSrc: text("hero_image_src"),
    openingStatement: text("opening_statement"),
    mainImage: text("main_image"),
    mainVideo: text("main_video"),

    preHeading: text("pre_heading"),
    title: text("title"),
    description: text("introduction"),

    facebookProfile: text("facebook_profile"),
    instagramProfile: text("instagram_profile"),
    linkedinProfile: text("linkedin_profile"),
    website: text("website"),
    youtubeChannel: text("youtube_channel"),

    whyTitle: text("why_title"),
    whyContent: text("why_content"),

    // Profile-level challenges section (02 · Challenges). Mirrors the same
    // pattern as outcomes: previously the section was driven by
    // `offer.strugglesHeadline` / `offer.strugglesDescription`, which are gated
    // behind Feature.OfferPage and therefore unavailable to Standard coaches.
    // Moving it to the profile makes the highest-leverage "before-state" copy
    // available to every coach. The CIH challenge tiles still render below as
    // a secondary tile grid.
    challengesHeadline: text("challenges_headline"),
    challengesDescription: text("challenges_description"),

    // Profile-level outcomes section (03 · Outcomes). Replaces the previous
    // dependency on `offer.resultsHeadline` / `offer.resultsDescription` for
    // section rendering — those fields are gated behind Feature.OfferPage so
    // Standard coaches couldn't populate them. The tile body still comes from
    // the `coaching_results` table.
    outcomesHeadline: text("outcomes_headline"),
    outcomesDescription: text("outcomes_description"),

    ctaButtonText: text("cta_button_text").default("Book a discovery call"),
    contactMode: text("contact_mode", {
      enum: ["booking", "messaging"],
    }).default("messaging"),

    leadMagnetTitle: text("lead_magnet_title"),
    leadMagnetDescription: text("lead_magnet_description"),
    leadMagnetCtaText: text("lead_magnet_cta_text"),
    leadMagnetDownloadCtaText: text("lead_magnet_download_cta_text"),
    leadMagnetUrl: text("lead_magnet_url"),

    ...timestamps,
  },
  (table) => [
    // Partial index on (closest_major_city) WHERE is_public = true powers the
    // sitemap + per-city coach listing queries without bloating write paths for
    // private profiles.
    index("profile_public_city_idx")
      .on(table.closestMajorCity)
      .where(sql`${table.isPublic} = true`),
    // Partial index to satisfy queries that filter by is_public and then
    // join by coach_id (the sitemap coverage query, most admin listings).
    index("profile_public_coach_idx")
      .on(table.coachId)
      .where(sql`${table.isPublic} = true`),
  ],
);

export type ProfileInsert = typeof profiles.$inferInsert;
export type ProfileSelect = typeof profiles.$inferSelect;

export const coachEmbeddings = pgTable(
  "CoachEmbeddings",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    type: coachEmbeddingTypeEnum("type").notNull(),
    embedding: vector("embedding", { dimensions: 1536 }).notNull(),
  },
  (table) => ({
    embeddingIndex: index("embedding_index").using(
      "hnsw",
      table.embedding.op("vector_cosine_ops"),
    ),
    uniqueCoachType: uniqueIndex("coach_type_unique").on(
      table.coachId,
      table.type,
    ),
  }),
);

export const coachDetails = pgTable("CoachDetails", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" })
    .unique(),

  dateOfBirth: timestamp("date_of_birth"),
  gender: text("gender"),

  mainCoachingCategory: text("main_coaching_category"),
  commonChallenges: text("common_challenges"),
  typicalTimeline: text("typical_timeline"),
  typesOfCoaching: text("types_of_coaching").array(),
  typesOfClients: text("types_of_clients").array(),
  typesOfProblems: text("types_of_problems").array(),

  // Coaching Format & Delivery
  coachingFormats: text("coaching_formats").array().default([]),
  sessionDeliveryMethods: text("session_delivery_methods").array().default([]),

  // Experience Level & Coaching Packages
  experienceLevel: text("experience_level"),

  // Coaching Style & Approach
  coachingApproaches: text("coaching_approaches").array().default([]),

  // Faith-Based Coaching
  faithIntegration: text("faith_integration").array().default([]),

  // Personality Profile
  discType: text("disc_type"),
  mbtiType: text("mbti_type"),
  enneagramType: text("enneagram_type"),
  humanDesignType: text("human_design_type"),

  joinedCommunityAt: timestamp("joined_community_at", { mode: "date" }),
});

export type CoachDetailsInsert = typeof coachDetails.$inferInsert;
export type CoachDetailsSelect = typeof coachDetails.$inferSelect;

export const certifications = pgTable("Certifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  issuer: text("issuer").notNull(),
  imageSrc: text("image_src"),
  verified: boolean("verified").notNull(),
  url: text("url"),
  order: integer("order").notNull().default(0),
});

export type CertificationsInsert = typeof certifications.$inferInsert;
export type CertificationsSelect = typeof certifications.$inferSelect;

export const ghlAgencyTokens = pgTable("GHLAgencyToken", {
  id: uuid("id").primaryKey().defaultRandom(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
  companyId: text("company_id").notNull(),
  locationId: text("location_id"),
  userId: text("user_id"),
  tokenType: text("token_type", { enum: ["Company", "Location"] }).notNull(),
  ...timestamps,
});

export type GHLAgencyTokenInsert = typeof ghlAgencyTokens.$inferInsert;
export type GHLAgencyTokenSelect = typeof ghlAgencyTokens.$inferSelect;

export const coachingResults = pgTable("CoachingResults", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  description: text("description"),
  icon: text("icon").notNull(),
  order: integer("order").notNull().default(0),
});

export type CoachingResultsInsert = typeof coachingResults.$inferInsert;
export type CoachingResultsSelect = typeof coachingResults.$inferSelect;

export const invoices = pgTable("Invoice", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id),

  ...timestamps,
  dueOn: timestamp("due_on").notNull(),
  paidOn: timestamp("paid_on"),

  customerName: text("customer_name").notNull(),
  customerEmail: text("customer_email").notNull(),
  description: text("description").notNull(),
  amount: integer("amount").notNull(), // in cents
  currency: text("currency").notNull(),
  status: text("status").notNull(),
  engagementId: uuid("engagement_id").references(() => engagements.id, {
    onDelete: "set null",
  }),
  stripeInvoiceId: text("stripe_invoice_id").notNull(),
  stripeChargeId: text("stripe_charge_id"),
  url: text("url"),

  // Transfer tracking for separate charges + transfers flow
  stripeTransferId: text("stripe_transfer_id"),
  stripeFee: integer("stripe_fee"), // Actual Stripe processing fee in cents
  platformFee: integer("platform_fee"), // Platform commission in cents
  transferAmount: integer("transfer_amount"), // Amount transferred to coach in cents
  platformFeeBps: integer("platform_fee_bps"), // Fee rate used (basis points, for audit)
  /**
   * An address change voided this invoice but failed before reissuing it;
   * the next change reissues it to the corrected address.
   */
  reissuePending: boolean("reissue_pending").notNull().default(false),
  settlementCurrency: text("settlement_currency"), // Currency for fee breakdown (may differ from invoice currency)
});

export type InvoiceInsert = typeof invoices.$inferInsert;
export type InvoiceSelect = typeof invoices.$inferSelect;

export const resources = pgTable(
  "Resource",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),

    title: text("title").notNull(),
    content: text("content").notNull(),
    description: text("description").notNull(),
    imageSrc: text("image_src"),
    publishedAt: timestamp("published_at", { mode: "date" }).defaultNow(),

    externalLinkText: text("external_link_text"),
    externalLinkUrl: text("external_link_url"),
    ...timestamps,
  },
  (table) => [
    // Every resource lookup in the sitemap and coach profile is keyed by
    // coach_id; postgres does not index foreign keys automatically.
    index("resource_coach_id_idx").on(table.coachId),
  ],
);

export type ResourceInsert = typeof resources.$inferInsert;
export type ResourceSelect = typeof resources.$inferSelect;

export const coachPoints = pgTable("CoachPoints", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),
  points: integer("points").notNull(),
  type: text("type").notNull(), // 'earned' or 'redeemed'
  description: text("description").notNull(),
  ...timestamps,
});

export type CoachPointsInsert = typeof coachPoints.$inferInsert;
export type CoachPointsSelect = typeof coachPoints.$inferSelect;

// Users table
export const users = pgTable(
  "User",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    email: text("email").unique(),
    firstName: text("first_name"),
    lastName: text("last_name"),
    phoneNumber: text("phone_number"),
    profilePictureUrl: text("profile_picture_url"),
    clerkUserId: text("clerk_user_id").unique(),
    workflowInstanceId: varchar("workflow_instance_id", { length: 160 }),
    scheduledForDeletionAt: timestamp("scheduled_for_deletion_at", {
      mode: "date",
    }),
    title: text("title"),
    bio: text("bio"),
    // Generic user-to-user referrals: each user has a shareable code, and
    // referredByUserId records who referred them (captured at signup).
    referralCode: text("referral_code").unique(),
    referredByUserId: uuid("referred_by_user_id").references(
      (): AnyPgColumn => users.id,
      { onDelete: "set null" },
    ),
    // Coach-onboarding-funnel intent fork: "client" | "coach", written pre-signup
    // by the intent cookie or backfilled by inference for pre-launch accounts.
    // Null means "unresolved" — the fork gate in `_user` prompts for it.
    primaryIntent: text("primary_intent"),
    // A place-based IANA zone she chose on the Settings page, under the name
    // Intl gives it (`timeZoneSettingSchema`). Null means she never chose one,
    // and the browser's zone applies.
    timeZone: text("time_zone"),
    // When she last opened What's new or closed its NOVA Home card. One time
    // for the whole app; null means never.
    whatsNewSeenAt: timestamp("whats_new_seen_at", { withTimezone: true }),
    // Set when the score funnel's signed-out trial start created this account
    // from an email nobody has proven. Cleared when she sets her password.
    // While it is set, nothing is written to GoHighLevel for her (the CRM
    // hold in `~/lib/server/crm-hold`).
    accountUnfinishedSince: timestamp("account_unfinished_since", {
      mode: "date",
      withTimezone: true,
    }),
    ...timestamps,
  },
  (table) => [
    // Functional index to support case-insensitive email lookups
    // (`where lower(email) = $1`) without sequential scans. Emails are
    // stored with their original case so the plain unique index on `email`
    // can't be used for the lower() lookup.
    index("user_lower_email_idx").on(sql`lower(${table.email})`),
  ],
);

export type UserInsert = typeof users.$inferInsert;
export type UserSelect = typeof users.$inferSelect;

/**
 * Which admin role each user holds. Roles and the permissions they grant are
 * defined in code (`src/lib/shared/roles.ts`); `role` is a key there. A key
 * code no longer defines grants nothing but stays visible. `grantedByUserId`
 * is null for roles copied from Clerk, which have no giver.
 */
export const userRoles = pgTable(
  "UserRole",
  {
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    role: text("role").notNull(),
    grantedByUserId: uuid("granted_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    grantedAt: timestamp("granted_at", { mode: "date" }).notNull().defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.userId, table.role] })],
);

export const userPreferences = pgTable(
  "UserPreferences",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    goals: text("goals").array().notNull().default([]),
    otherGoal: text("other_goal"),
    startTime: text("start_time").notNull().default(""),
    support: text("support").notNull().default(""),

    coachingFormats: text("coaching_formats").notNull().default(""),
    coachingApproaches: text("coaching_approaches")
      .array()
      .notNull()
      .default([]),
    coachingPackages: text("coaching_packages").array().notNull().default([]),
    coachGender: text("coach_gender").notNull().default(""),
    coachAges: text("coach_ages").array().notNull().default([]),
    userPersonality: text("user_rersonality").array().notNull().default([]),
    coachPersonality: text("coach_rersonality").array().notNull().default([]),
    sessionDeliveryMethods: text("session_delivery_methods")
      .notNull()
      .default(""),
    prefersCoachLocation: text("prefers_coach_location")
      .notNull()
      .default("No preference"),
    coachLocationCountry: text("coach_location_country"),
    coachLocationRegion: text("coach_location_region"),
    faithIntegration: text("faith_integration").array().notNull().default([]),
    otherFaithIntegration: text("other_faith_integration"),
    preferredLanguages: text("preferred_languages")
      .array()
      .notNull()
      .default([]),
    personalContext: text("personal_context"),
    ...timestamps,
    preferencesEmbedding: vector("preferences_embedding", {
      dimensions: 1536,
    }).notNull(),
  },
  (table) => ({
    preferencesEmbeddingIndex: index("preferences_embedding_index").using(
      "hnsw",
      table.preferencesEmbedding.op("vector_cosine_ops"),
    ),
  }),
);

export type UserPreferencesInsert = typeof userPreferences.$inferInsert;
export type UserPreferencesSelect = typeof userPreferences.$inferSelect;

export const appointments = pgTable("Appointment", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  ghlAppointmentId: text("ghl_appointment_id").notNull().unique(),
  ghlContactId: text("ghl_contact_id").notNull(),
  ghlCalendarId: text("ghl_calendar_id").notNull(),

  status: appointmentStatusEnum("status").notNull(),
  startTime: timestamp("start_time", { mode: "date" }).notNull(),
  endTime: timestamp("end_time", { mode: "date" }).notNull(),

  title: text("title").notNull(),
  description: text("description"),

  metadata: text("metadata"), // JSON stringified GHL-specific data

  ...timestamps,
});

export type AppointmentInsert = typeof appointments.$inferInsert;
export type AppointmentSelect = typeof appointments.$inferSelect;

export const expertiseCategories = pgTable("expertise_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull().unique(),
  listingName: text("listing_name"), // New column for plural/listing contexts
  slug: text("slug").notNull().unique(),
  icon: text("icon").$type<TIconName>(), // Icon name for NamedIcon
  description: text("description"), // Optional description for the category
});

export type ExpertiseCategoryInsert = typeof expertiseCategories.$inferInsert;
export type ExpertiseCategorySelect = typeof expertiseCategories.$inferSelect;

export const expertiseSubcategories = pgTable("expertise_subcategories", {
  id: uuid("id").primaryKey().defaultRandom(),
  categoryId: uuid("category_id")
    .notNull()
    .references(() => expertiseCategories.id),
  name: text("name").notNull().unique(),
  listingName: text("listing_name"), // New column for plural/listing contexts
  slug: text("slug").notNull().unique(),
});

export type ExpertiseSubcategoryInsert =
  typeof expertiseSubcategories.$inferInsert;
export type ExpertiseSubcategorySelect =
  typeof expertiseSubcategories.$inferSelect;

export const coachExpertise = pgTable(
  "coach_expertise",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    subcategoryId: uuid("subcategory_id")
      .notNull()
      .references(() => expertiseSubcategories.id, { onDelete: "cascade" }),
    type: expertiseTypeEnum("type").notNull().default(ExpertiseType.Secondary),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.subcategoryId] }),
    index("idx_subcat").on(table.subcategoryId),
    index("idx_type").on(table.type),
  ],
);

export type CoachExpertiseInsert = typeof coachExpertise.$inferInsert;
export type CoachExpertiseSelect = typeof coachExpertise.$inferSelect;

// Coach Certifying Organizations (CCOs)
export const ccos = pgTable(
  "ccos",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    slug: text("slug"),
    website: text("website"),
    logoUrl: text("logo_url"),
    description: text("description"),
    type: ccoTypeEnum("type").notNull().default(CCOType.CTO),
    subcategory: text("subcategory"),
    founder: text("founder"),
    founderUrl: text("founder_url"),
    body: jsonb("body").$type<JsonValue>(),
    headquartersCountry: text("headquarters_country"),
    foundedYear: integer("founded_year"),
    accreditedBy: text("accredited_by"),
    missionStatement: text("mission_statement"),
    differentiator: text("differentiator"),
    narrativeStats: jsonb("narrative_stats").$type<NarrativeStat[]>(),
    hidePlatformStats: boolean("hide_platform_stats").notNull().default(false),
    pullQuote: text("pull_quote"),
    pullQuoteAttribution: text("pull_quote_attribution"),
    socialLinks: jsonb("social_links").$type<{
      linkedin?: string;
      instagram?: string;
      youtube?: string;
      facebook?: string;
      twitter?: string;
    }>(),
    faqs: jsonb("faqs").$type<{ question: string; answer: string }[]>(),
    draftOf: uuid("draft_of").references((): AnyPgColumn => ccos.id, {
      onDelete: "cascade",
    }),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("ccos_name_unique")
      .on(table.name)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("ccos_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("ccos_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("cco_name_idx").on(table.name),
    index("cco_slug_idx").on(table.slug),
    index("cco_status_idx").on(table.status),
  ],
);

export type CcoInsert = typeof ccos.$inferInsert;
export type CcoSelect = typeof ccos.$inferSelect;

// Join table for coaches and CCOs
/**
 * How a coach's membership or program claim was decided, shared by
 * `coaches_ccos` and `coaches_ctps`. Rules live in `src/lib/server/cco-claims.ts`.
 */
const claimVerificationColumns = () => ({
  verificationState: claimVerificationStateEnum("verification_state")
    .notNull()
    .default(ClaimVerificationState.Waiting),
  decidedByUserId: uuid("decided_by_user_id").references(() => users.id, {
    onDelete: "set null",
  }),
  /** The organization whose representative decided; null means DCM staff. */
  decidedByCcoId: uuid("decided_by_cco_id").references(() => ccos.id, {
    onDelete: "set null",
  }),
  decidedAt: timestamp("decided_at", { mode: "date" }),
  /** What profiles show as month and year; optional. */
  verificationDate: date("verification_date", { mode: "string" }),
  decisionNote: text("decision_note"),
  reviewRequestedAt: timestamp("review_requested_at", { mode: "date" }),
  reviewMessage: text("review_message"),
  /**
   * The coach removed a not-confirmed claim. The row stays so re-adding it
   * keeps the decision.
   */
  removedByCoachAt: timestamp("removed_by_coach_at", { mode: "date" }),
});

export const coachesCcos = pgTable(
  "coaches_ccos",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    membershipVerifiedAt: timestamp("membership_verified_at", { mode: "date" }),
    ...claimVerificationColumns(),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.ccoId] }),
    index("cco_coach_idx").on(table.coachId),
    index("cco_cco_idx").on(table.ccoId),
  ],
);

export type CoachCcoInsert = typeof coachesCcos.$inferInsert;
export type CoachCcoSelect = typeof coachesCcos.$inferSelect;

// A person who represents an organization: a normal account linked to a
// published `ccos` row, scoped to that organization and granting nothing in
// /admin. Removing a representative deletes the row.
export const ccoRepresentatives = pgTable(
  "cco_representatives",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    /** The person's job title at the organization. */
    roleTitle: text("role_title"),
    addedByUserId: uuid("added_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("cco_representatives_cco_user_unique").on(
      table.ccoId,
      table.userId,
    ),
    index("cco_representatives_user_idx").on(table.userId),
  ],
);

// An emailed invitation to represent an organization. Only the token's
// SHA-256 hash is stored; the raw token lives in the email link alone.
export const ccoRepresentativeInvites = pgTable(
  "cco_representative_invites",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    name: text("name"),
    tokenHash: text("token_hash").notNull().unique(),
    expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
    invitedByUserId: uuid("invited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    acceptedAt: timestamp("accepted_at", { mode: "date" }),
    acceptedByUserId: uuid("accepted_by_user_id").references(
      () => users.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => [index("cco_representative_invites_cco_idx").on(table.ccoId)],
);

// A signed-in person asking to represent an organization. One pending request
// per person and organization; decided requests stay as the record.
export const ccoAccessRequests = pgTable(
  "cco_access_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    roleTitle: text("role_title").notNull(),
    message: text("message"),
    status: ccoAccessRequestStatusEnum("status")
      .notNull()
      .default(CcoAccessRequestStatus.Pending),
    decidedByUserId: uuid("decided_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    decidedAt: timestamp("decided_at", { mode: "date" }),
    decisionNote: text("decision_note"),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("cco_access_requests_pending_unique")
      .on(table.ccoId, table.userId)
      .where(sql`${table.status} = 'pending'`),
    index("cco_access_requests_cco_idx").on(table.ccoId),
  ],
);

// Coaching Training Programs (CTPs)
export const ctps = pgTable(
  "ctps",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    slug: text("slug"),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "restrict" }),
    website: text("website"),
    logoUrl: text("logo_url"),
    description: text("description"),
    deliveryFormat: text("delivery_format"),
    accreditationLevel: text("accreditation_level"),
    duration: text("duration"),
    tuitionRange: text("tuition_range"),
    body: jsonb("body").$type<JsonValue>(),
    prerequisites: text("prerequisites"),
    icfHours: text("icf_hours"),
    narrativeStats: jsonb("narrative_stats").$type<NarrativeStat[]>(),
    hidePlatformStats: boolean("hide_platform_stats").notNull().default(false),
    pullQuote: text("pull_quote"),
    pullQuoteAttribution: text("pull_quote_attribution"),
    faqs: jsonb("faqs").$type<{ question: string; answer: string }[]>(),
    draftOf: uuid("draft_of").references((): AnyPgColumn => ctps.id, {
      onDelete: "cascade",
    }),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("ctps_name_unique")
      .on(table.name)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("ctps_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("ctps_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("ctp_name_idx").on(table.name),
    index("ctp_slug_idx").on(table.slug),
    index("ctp_cco_idx").on(table.ccoId),
    index("ctp_status_idx").on(table.status),
  ],
);

export type CtpInsert = typeof ctps.$inferInsert;
export type CtpSelect = typeof ctps.$inferSelect;

// The staff change history of organization and program pages. Publishing
// deletes the draft, so each send, publish, return and discard is written
// here when it happens, with every changed field before and after. Rules live
// in `src/lib/server/page-changes.ts`.
export const pageChangeLog = pgTable(
  "page_change_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /** The published organization; for a program, the organization running it. */
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    /** The published program, when the change is to a program page. */
    ctpId: uuid("ctp_id").references(() => ctps.id, { onDelete: "set null" }),
    pageType: pageChangePageTypeEnum("page_type").notNull(),
    /** The page's name when it changed, kept if the page goes. */
    pageName: text("page_name").notNull(),
    event: pageChangeEventEnum("event").notNull(),
    actorUserId: uuid("actor_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    actorRole: pageChangeActorRoleEnum("actor_role").notNull(),
    /** The representative whose edit a staff decision was about. */
    forUserId: uuid("for_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /** The note staff returned the draft with. */
    note: text("note"),
    changes: jsonb("changes")
      .$type<{ field: string; before: string | null; after: string | null }[]>()
      .notNull()
      .default([]),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => [
    index("page_change_log_cco_created_idx").on(table.ccoId, table.createdAt),
    index("page_change_log_ctp_idx").on(table.ctpId),
    index("page_change_log_created_idx").on(table.createdAt),
  ],
);

// Join table for coaches and CTPs
export const coachesCtps = pgTable(
  "coaches_ctps",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    ctpId: uuid("ctp_id")
      .notNull()
      .references(() => ctps.id, { onDelete: "cascade" }),
    certificationDate: timestamp("certification_date", { mode: "date" }),
    verified: boolean("verified").notNull().default(false),
    ...claimVerificationColumns(),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.ctpId] }),
    index("ctp_coach_idx").on(table.coachId),
    index("ctp_ctp_idx").on(table.ctpId),
  ],
);

export type CoachCtpInsert = typeof coachesCtps.$inferInsert;
export type CoachCtpSelect = typeof coachesCtps.$inferSelect;

// An organization's suggestion that a coach add its membership (no program)
// or one of its programs, sent when the coach's email was on a graduate
// list. The list itself is never stored: a row holds only this coach, the
// target and the date the list gave. Accepting consumes the row; dismissing
// keeps it without the date, so a later list doesn't email the coach again.
// Pending rows are purged once expired.
export const coachClaimSuggestions = pgTable(
  "coach_claim_suggestions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    ctpId: uuid("ctp_id").references(() => ctps.id, { onDelete: "cascade" }),
    listDate: date("list_date", { mode: "string" }),
    suggestedByUserId: uuid("suggested_by_user_id").references(
      () => users.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
    expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
    dismissedAt: timestamp("dismissed_at", { mode: "date" }),
  },
  (table) => [
    unique("coach_claim_suggestions_target_unique")
      .on(table.coachId, table.ccoId, table.ctpId)
      .nullsNotDistinct(),
  ],
);

export const cities = pgTable("cities", {
  id: uuid("id").primaryKey().defaultRandom(),
  slug: text("slug").notNull().unique(),
  label: text("label").notNull(),
  country: text("country").notNull(),
  region: text("region").notNull(),
  latitude: doublePrecision("latitude").notNull(),
  longitude: doublePrecision("longitude").notNull(),
});

export type CityInsert = typeof cities.$inferInsert;
export type CitySelect = typeof cities.$inferSelect;

export const coachGroupPages = pgTable(
  "coach_group_pages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    groupType: text("group_type").notNull(),
    citySlug: text("city_slug").references(() => cities.slug),
    categorySlug: text("category_slug").references(
      () => expertiseCategories.slug,
    ),
    subcategorySlug: text("subcategory_slug").references(
      () => expertiseSubcategories.slug,
    ),
    seoTitle: text("seo_title"),
    metaDescription: text("meta_description"),
    contentMd: text("content_md"),
    faqItems: jsonb("faq_items").$type<
      {
        question: string;
        answer: string;
      }[]
    >(),
    ...timestamps,
  },
  (table) => ({
    uniqueGroup: uniqueIndex("coach_group_pages_unique").on(
      table.groupType,
      table.citySlug,
      table.categorySlug,
      table.subcategorySlug,
    ),
  }),
);

export type CoachGroupPageInsert = typeof coachGroupPages.$inferInsert;
export type CoachGroupPageSelect = typeof coachGroupPages.$inferSelect;

export const conversations = pgTable(
  "conversations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    subject: text("subject").notNull(),
    lastMessageAt: timestamp("last_message_at", { mode: "date" }).defaultNow(),
    ...timestamps,
  },
  (table) => ({
    lastMessageAtIdx: index("conversations_last_message_at_idx").on(
      table.lastMessageAt,
    ),
  }),
);

export type ConversationInsert = typeof conversations.$inferInsert;
export type ConversationSelect = typeof conversations.$inferSelect;

export const conversationParticipants = pgTable(
  "conversation_participants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => conversations.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    lastReadAt: timestamp("last_read_at", { mode: "date" }),
    joinedAt: timestamp("joined_at", { mode: "date" }).defaultNow(),
  },
  (table) => ({
    conversationIdIdx: index(
      "conversation_participants_conversation_id_idx",
    ).on(table.conversationId),
    userIdIdx: index("conversation_participants_user_id_idx").on(table.userId),
    uniqueParticipant: uniqueIndex(
      "conversation_participants_conversation_user_unique",
    ).on(table.conversationId, table.userId),
  }),
);

export type ConversationParticipantInsert =
  typeof conversationParticipants.$inferInsert;
export type ConversationParticipantSelect =
  typeof conversationParticipants.$inferSelect;

export const contacts = pgTable(
  "contacts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ownerId: uuid("owner_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    contactUserId: uuid("contact_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => ({
    ownerIdIdx: index("contacts_owner_id_idx").on(table.ownerId),
    contactUserIdIdx: index("contacts_contact_user_id_idx").on(
      table.contactUserId,
    ),
    uniqueContact: uniqueIndex("contacts_owner_contact_unique").on(
      table.ownerId,
      table.contactUserId,
    ),
  }),
);

export type ContactInsert = typeof contacts.$inferInsert;
export type ContactSelect = typeof contacts.$inferSelect;

export const messages = pgTable(
  "messages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => conversations.id, { onDelete: "cascade" }),
    senderUserId: uuid("sender_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    content: text("content").notNull(),
    ...timestamps,
  },
  (table) => ({
    conversationIdIdx: index("messages_conversation_id_idx").on(
      table.conversationId,
    ),
    senderUserIdIdx: index("messages_sender_user_id_idx").on(
      table.senderUserId,
    ),
    createdAtIdx: index("messages_created_at_idx").on(table.createdAt),
  }),
);

export type MessageInsert = typeof messages.$inferInsert;
export type MessageSelect = typeof messages.$inferSelect;

// Summit-related tables
export const summits = pgTable(
  "summits",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    airtableId: text("airtable_id"), // Airtable record ID for stable identification
    title: text("title").notNull(),
    description: text("description"),

    // Scheduling
    startDate: timestamp("start_date", { mode: "date" }).notNull(),
    endDate: timestamp("end_date", { mode: "date" }).notNull(),

    // CTA button (appears at specified time during summit)
    ctaText: text("cta_text"),
    ctaUrl: text("cta_url"),
    ctaAppearsOn: timestamp("cta_appears_on", { mode: "date" }),

    ...timestamps,
  },
  (table) => ({
    airtableIdUnique: uniqueIndex("summits_airtable_id_unique").on(
      table.airtableId,
    ),
  }),
);

export type SummitInsert = typeof summits.$inferInsert;
export type SummitSelect = typeof summits.$inferSelect;

export const summitSpeakers = pgTable(
  "summit_speakers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    airtableId: text("airtable_id"), // Airtable record ID for stable identification
    name: text("name").notNull(),
    title: text("title").notNull(),
    bio: text("bio"),
    avatarUrl: text("avatar_url"),

    // Social links
    linkedInUrl: text("linkedin_url"),
    twitterUrl: text("twitter_url"),
    instagramUrl: text("instagram_url"),
    websiteUrl: text("website_url"),

    ...timestamps,
  },
  (table) => ({
    airtableIdUnique: uniqueIndex("summit_speakers_airtable_id_unique").on(
      table.airtableId,
    ),
  }),
);

export type SummitSpeakerInsert = typeof summitSpeakers.$inferInsert;
export type SummitSpeakerSelect = typeof summitSpeakers.$inferSelect;

export const summitSessions = pgTable(
  "summit_sessions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    airtableId: text("airtable_id"),
    summitId: uuid("summit_id")
      .notNull()
      .references(() => summits.id, { onDelete: "cascade" }),
    speakerId: uuid("speaker_id")
      .notNull()
      .references(() => summitSpeakers.id, { onDelete: "cascade" }),
    summitDayId: uuid("summit_day_id")
      .notNull()
      .references(() => summitDays.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id").references(() => coaches.id),

    // Basic info
    title: text("title").notNull(),
    isPublished: boolean("is_published").notNull().default(true),
    description: text("description").notNull(),
    fullDescription: text("full_description"),

    // Scheduling
    startTimeUtc: timestamp("start_time_utc", { mode: "date" }).notNull(),
    durationMinutes: integer("duration_minutes").notNull(),

    // Access control
    isVipOnly: boolean("is_vip_only").notNull().default(false),
    maxAttendees: integer("max_attendees"),

    // Content
    recordingUrl: text("recording_url"),
    liveUrl: text("live_url"),
    sessionFormat: text("session_format")
      .notNull()
      .default("presentation")
      .$type<SummitSessionFormat>(),

    ...timestamps,
  },
  (table) => ({
    airtableIdUnique: uniqueIndex("summit_sessions_airtable_id_unique").on(
      table.airtableId,
    ),
    summitIdIdx: index("summit_sessions_summit_id_idx").on(table.summitId),
    speakerIdIdx: index("summit_sessions_speaker_id_idx").on(table.speakerId),
    startTimeUtcIdx: index("summit_sessions_start_time_utc_idx").on(
      table.startTimeUtc,
    ),
    coachIdIdx: index("summit_sessions_coach_id_idx").on(table.coachId),
  }),
);

export type SummitSessionInsert = typeof summitSessions.$inferInsert;
export type SummitSessionSelect = typeof summitSessions.$inferSelect;

export const summitSessionTopics = pgTable(
  "summit_session_topics",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sessionId: uuid("session_id")
      .notNull()
      .references(() => summitSessions.id, { onDelete: "cascade" }),
    topic: text("topic").notNull(),
  },
  (table) => ({
    sessionIdIdx: index("summit_session_topics_session_id_idx").on(
      table.sessionId,
    ),
    topicIdx: index("summit_session_topics_topic_idx").on(table.topic),
  }),
);

export type SummitSessionTopicInsert = typeof summitSessionTopics.$inferInsert;
export type SummitSessionTopicSelect = typeof summitSessionTopics.$inferSelect;

export const summitDays = pgTable(
  "summit_days",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    summitId: uuid("summit_id")
      .notNull()
      .references(() => summits.id, { onDelete: "cascade" }),

    // Day info
    date: timestamp("date", { mode: "date" }).notNull(),
    title: text("title").notNull(), // "Day 1: Foundations"
    description: text("description"),

    // Zoom integration
    liveUrl: text("live_url").notNull(),

    ...timestamps,
  },
  (table) => ({
    summitIdIdx: index("summit_days_summit_id_idx").on(table.summitId),
    dateIdx: index("summit_days_date_idx").on(table.date),
    summitDateUnique: uniqueIndex("summit_days_summit_date_unique").on(
      table.summitId,
      table.date,
    ),
  }),
);

export type SummitDayInsert = typeof summitDays.$inferInsert;
export type SummitDaySelect = typeof summitDays.$inferSelect;

export const summitRegistrations = pgTable(
  "summit_registrations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    summitId: uuid("summit_id")
      .notNull()
      .references(() => summits.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),

    // Registration details
    registeredAt: timestamp("registered_at", { mode: "date" }).defaultNow(),
    isVipRegistration: boolean("is_vip_registration").notNull().default(false),

    ...timestamps,
  },
  (table) => ({
    summitUserIdx: uniqueIndex("summit_registrations_summit_user_idx").on(
      table.summitId,
      table.userId,
    ),
    summitIdIdx: index("summit_registrations_summit_id_idx").on(table.summitId),
    userIdIdx: index("summit_registrations_user_id_idx").on(table.userId),
    registeredAtIdx: index("summit_registrations_registered_at_idx").on(
      table.registeredAt,
    ),
  }),
);

export type SummitRegistrationInsert = typeof summitRegistrations.$inferInsert;
export type SummitRegistrationSelect = typeof summitRegistrations.$inferSelect;

export const summitSessionRegistrations = pgTable(
  "summit_session_registrations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sessionId: uuid("session_id")
      .notNull()
      .references(() => summitSessions.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (table) => ({
    sessionUserIdx: uniqueIndex(
      "summit_session_registrations_session_user_idx",
    ).on(table.sessionId, table.userId),
    sessionIdIdx: index("summit_session_registrations_session_id_idx").on(
      table.sessionId,
    ),
    userIdIdx: index("summit_session_registrations_user_id_idx").on(
      table.userId,
    ),
  }),
);

export type SummitSessionRegistrationInsert =
  typeof summitSessionRegistrations.$inferInsert;
export type SummitSessionRegistrationSelect =
  typeof summitSessionRegistrations.$inferSelect;

export const engagements = pgTable(
  "Engagement",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    description: text("description"),
    encryptedDescription: text("encrypted_description"),
    iv: text("iv"),
    /** Coach asked (at invite time) that the client share initial goals during onboarding. */
    onboardingGoalsRequested: boolean("onboarding_goals_requested")
      .notNull()
      .default(false),
    /** Set when the client completes the welcome wizard. */
    onboardedAt: timestamp("onboarded_at", { mode: "date" }),
    /**
     * The program created its client's account for an address the coach
     * typed. Until someone signs in to it, the coach may resend the
     * invitation or correct the address.
     */
    clientAccountCreated: boolean("client_account_created")
      .notNull()
      .default(false),
    /** When the client was last emailed an invitation; resend waits 10 minutes after it. */
    invitationSentAt: timestamp("invitation_sent_at", { mode: "date" }),
    /**
     * Clerk sign-in tokens in the invitations this program sent. Changing the
     * address revokes them, so the old links stop signing anyone in.
     */
    invitationTokenIds: text("invitation_token_ids")
      .array()
      .notNull()
      .default([]),
    /**
     * Replacement invoices an unfinished address change created on Stripe and
     * never sent. The next change voids them first.
     */
    addressChangeInvoiceIds: text("address_change_invoice_ids")
      .array()
      .notNull()
      .default([]),
    /** Set while an address change runs, so two never run at once. */
    addressChangeStartedAt: timestamp("address_change_started_at", {
      mode: "date",
    }),
    status: engagementStatusEnum("status")
      .notNull()
      .default(EngagementStatus.ACTIVE),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    dateStart: timestamp("date_start", { mode: "date" }).notNull(),
    dateEnd: timestamp("date_end", { mode: "date" }).notNull(),
    ...timestamps,
  },
  (table) => ({
    coachIdIdx: index("engagement_coach_id_idx").on(table.coachId),
    userIdIdx: index("engagement_user_id_idx").on(table.userId),
  }),
);

export type EngagementInsert = typeof engagements.$inferInsert;
export type EngagementSelect = typeof engagements.$inferSelect;

export const sessions = pgTable(
  "Session",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    title: text("title"),
    status: sessionStatusEnum("status")
      .notNull()
      .default(SessionStatus.SCHEDULED),
    startAt: timestamp("start_at", { mode: "date" }).notNull(),
    duration: integer("duration").notNull(),
    meetingUrl: text("meeting_url"),
    /** Who made `meetingUrl`: the coach's own link, or a Google Meet DCM asked for. */
    meetingProvider: meetingProviderEnum("meeting_provider")
      .notNull()
      .default(MeetingProvider.CUSTOM),
    /** The meeting's id at its provider (a Google Meet code); null for `custom`. */
    meetingExternalId: text("meeting_external_id"),
    /**
     * Where the client's calendar copy comes from, kept for the session's
     * life so the client never gets two copies. Null for sessions DCM never
     * offered to a connected calendar, which read as `ics`.
     */
    clientCalendarSource: clientCalendarSourceEnum("client_calendar_source"),
    keySummary: text("key_summary"),
    ...timestamps,
  },
  (table) => ({
    engagementIdIdx: index("session_engagement_id_idx").on(table.engagementId),
    uniqueSession: uniqueIndex("unique_session_per_engagement_start").on(
      table.engagementId,
      table.startAt,
    ),
  }),
);

export type SessionInsert = typeof sessions.$inferInsert;
export type SessionSelect = typeof sessions.$inferSelect;

export const tasks = pgTable(
  "Task",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /**
     * Who made the task. Null only for history whose creator has no user
     * account: homework from a coach without a linked user, or a creator who
     * deleted their account.
     */
    creatorUserId: uuid("creator_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /**
     * Who should do it, often the creator. The server allows another person
     * only inside an engagement that both share.
     */
    assigneeUserId: uuid("assignee_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    /** Links the task to a coaching engagement; homework always has one. */
    engagementId: uuid("engagement_id").references(() => engagements.id, {
      onDelete: "cascade",
    }),
    title: text("title").notNull(),
    description: text("description"),
    deadline: timestamp("deadline", { mode: "date" }),
    priority: integer("priority"),
    status: taskStatusEnum("status").default(TaskStatus.ACTIVE).notNull(),
    sessionId: uuid("session_id").references(() => sessions.id, {
      onDelete: "set null",
    }),
    /** The goal this action serves; powers the "moves: {goal}" pill. */
    goalId: uuid("goal_id").references(() => goals.id, {
      onDelete: "set null",
    }),
    /** Implementation-intention trigger: "after {routine} → {action}" (plaintext, like title/description). */
    trigger: text("trigger"),
    /** Completion reflection, E2E encrypted via the "homeworkReflection" key-grant. Holds the full JSON EncryptedData blob. */
    encryptedReflection: text("encrypted_reflection"),
    /** Identifier IV for the reflection (parity with goals/notes; not the decryption source). */
    reflectionIv: text("reflection_iv"),
    /** The wins-journal entry this reflection was promoted into; makes the promoted state durable. */
    promotedWinId: uuid("promoted_win_id").references(
      () => engagementNotes.id,
      { onDelete: "set null" },
    ),
    ...timestamps,
  },
  (table) => ({
    creatorUserIdIdx: index("task_creator_user_id_idx").on(table.creatorUserId),
    assigneeUserIdIdx: index("task_assignee_user_id_idx").on(
      table.assigneeUserId,
    ),
    engagementIdIdx: index("task_engagement_id_idx").on(table.engagementId),
    sessionIdIdx: index("task_session_id_idx").on(table.sessionId),
  }),
);

export type TaskInsert = typeof tasks.$inferInsert;
export type TaskSelect = typeof tasks.$inferSelect;

/**
 * Every goal belongs to one person (the client). A Life Design goal sits in a
 * life category; a step under a goal (a Life Design milestone) has a parent;
 * a coaching program says which goals it works on through `ProgramGoal`.
 * Each goal has its own key, wrapped per reader in `KeyGrants` under the
 * resource type "goal".
 */
export const goals = pgTable(
  "Goal",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    parentGoalId: uuid("parent_goal_id").references(
      (): AnyPgColumn => goals.id,
      { onDelete: "cascade" },
    ),
    categoryId: uuid("category_id").references(() => categoriesOfLife.id, {
      onDelete: "cascade",
    }),
    /** Plain title of a step written before steps were encrypted; cleared when the owner's browser encrypts it. */
    title: text("title"),
    encryptedTitle: text("encrypted_title"),
    /** Plain description of a program goal written before encryption; cleared when the owner's browser encrypts it. */
    description: text("description"),
    encryptedDescription: text("encrypted_description"),
    iv: text("iv"),
    /** Set on Life Design goals only. */
    timeline: goalTimelineEnum("timeline"),
    status: goalStatusEnum("status").default(GoalStatus.PLANNED).notNull(),
    deadline: timestamp("deadline", { mode: "date" }),
    /** Position among the steps under the same parent. */
    order: integer("order").notNull().default(0),
    /** Public link and the plain copy the owner published with it. */
    shareSlug: text("share_slug").unique(),
    shareTitle: text("share_title"),
    shareDescription: text("share_description"),
    shareCategoryName: text("share_category_name"),
    ...timestamps,
  },
  (table) => ({
    userIdIdx: index("goal_user_id_idx").on(table.userId),
    parentGoalIdIdx: index("goal_parent_goal_id_idx").on(table.parentGoalId),
    categoryIdIdx: index("goal_category_id_idx").on(table.categoryId),
    shareSlugIdx: index("goal_share_slug_idx").on(table.shareSlug),
  }),
);

export type GoalInsert = typeof goals.$inferInsert;
export type GoalSelect = typeof goals.$inferSelect;

/** A goal a coaching program works on. */
export const programGoals = pgTable(
  "ProgramGoal",
  {
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    goalId: uuid("goal_id")
      .notNull()
      .references(() => goals.id, { onDelete: "cascade" }),
    key: boolean("key").default(false),
    /** Client-suggested goals enter as "proposed" and need coach acceptance. */
    createdBy: text("created_by", { enum: ["coach", "user"] })
      .notNull()
      .default("coach"),
    status: text("status", { enum: ["proposed", "active"] })
      .notNull()
      .default("active"),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.engagementId, table.goalId] }),
    index("program_goal_goal_id_idx").on(table.goalId),
  ],
);

export type ProgramGoalInsert = typeof programGoals.$inferInsert;
export type ProgramGoalSelect = typeof programGoals.$inferSelect;

export const obstacles = pgTable(
  "Obstacle",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    description: text("description"),
    encryptedDescription: text("encrypted_description"),
    iv: text("iv"),
    key: boolean("key").default(false),
    ...timestamps,
  },
  (table) => ({
    engagementIdIdx: index("obstacle_engagement_id_idx").on(table.engagementId),
  }),
);

export type ObstacleInsert = typeof obstacles.$inferInsert;
export type ObstacleSelect = typeof obstacles.$inferSelect;

export const engagementNotes = pgTable(
  "EngagementNote",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    key: boolean("key").default(false),
    description: text("description"),
    encryptedDescription: text("encrypted_description"),
    iv: text("iv"),
    createdBy: text("created_by", { enum: ["coach", "user"] }).notNull(),
    /** "win" rows power the client's wins journal; "note" is the default freeform note. */
    kind: text("kind", { enum: ["note", "win"] })
      .notNull()
      .default("note"),
    visibility: boolean("visibility").default(false),
    ...timestamps,
  },
  (table) => ({
    engagementIdIdx: index("engagement_note_engagement_id_idx").on(
      table.engagementId,
    ),
  }),
);

export type EngagementNoteInsert = typeof engagementNotes.$inferInsert;
export type EngagementNoteSelect = typeof engagementNotes.$inferSelect;

export const sessionNotes = pgTable(
  "SessionNote",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sessionId: uuid("session_id")
      .notNull()
      .references(() => sessions.id, { onDelete: "cascade" }),
    description: text("description"),
    encryptedDescription: text("encrypted_description"),
    iv: text("iv"),
    createdBy: text("created_by", { enum: ["coach", "user"] })
      .notNull()
      .default("coach"),
    visibility: boolean("visibility").default(false),
    key: boolean("key").default(false),
    ...timestamps,
  },
  (table) => ({
    sessionIdIdx: index("session_note_id_idx").on(table.sessionId),
  }),
);

export type SessionNoteInsert = typeof sessionNotes.$inferInsert;
export type SessionNoteSelect = typeof sessionNotes.$inferSelect;

/**
 * Client pre-session check-in: answers to three reflection prompts submitted
 * before an upcoming session. One per session, client-authored, E2E encrypted
 * (key-granted to both client and coach via the "sessionCheckIn" resource type).
 */
export const sessionCheckIns = pgTable(
  "SessionCheckIn",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sessionId: uuid("session_id")
      .notNull()
      .references(() => sessions.id, { onDelete: "cascade" }),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    encryptedWins: text("encrypted_wins"),
    encryptedChallenges: text("encrypted_challenges"),
    encryptedFocus: text("encrypted_focus"),
    iv: text("iv"),
    ...timestamps,
  },
  (table) => ({
    sessionIdUnique: uniqueIndex("session_check_in_session_id_unique").on(
      table.sessionId,
    ),
    engagementIdIdx: index("session_check_in_engagement_id_idx").on(
      table.engagementId,
    ),
  }),
);

export type SessionCheckInInsert = typeof sessionCheckIns.$inferInsert;
export type SessionCheckInSelect = typeof sessionCheckIns.$inferSelect;

export const tags = pgTable(
  "Tag",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "cascade",
    }),
    userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => ({
    coachIdIdx: index("tag_coach_id_idx").on(table.coachId),
    userIdIdx: index("tag_user_id_idx").on(table.userId),
    nameCoachUnique: uniqueIndex("tag_name_coach_id_unique").on(
      table.name,
      table.coachId,
    ),
    nameUserUnique: uniqueIndex("tag_name_user_id_unique").on(
      table.name,
      table.userId,
    ),
    ownerXorCheck: check(
      "tag_owner_xor_check",
      sql`("coach_id" IS NULL AND "user_id" IS NOT NULL) OR ("coach_id" IS NOT NULL AND "user_id" IS NULL)`,
    ),
  }),
);

export type TagsInsert = typeof tags.$inferInsert;
export type TagsSelect = typeof tags.$inferSelect;

export const assignTagToTasks = pgTable(
  "AssignTagToTask",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    taskId: uuid("task_id")
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade" }),
    tagId: uuid("tag_id")
      .notNull()
      .references(() => tags.id, { onDelete: "cascade" }),
  },
  (table) => ({
    taskIdIdx: index("assign_tag_to_task_task_id_idx").on(table.taskId),
    tagIdIdx: index("assign_tag_to_task_tag_id_idx").on(table.tagId),
    taskTagUnique: uniqueIndex("assign_tag_to_task_task_id_tag_id_unique").on(
      table.taskId,
      table.tagId,
    ),
  }),
);

export type AssignTagToTaskInsert = typeof assignTagToTasks.$inferInsert;
export type AssignTagToTaskSelect = typeof assignTagToTasks.$inferSelect;

export const engagementResources = pgTable("EngagementResource", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),
  engagementId: uuid("engagement_id")
    .notNull()
    .references(() => engagements.id, { onDelete: "cascade" }),
  sessionId: uuid("session_id").references(() => sessions.id, {
    onDelete: "set null",
  }),
  title: text("title").notNull(),
  filename: text("filename"),
  uploadModel: text("upload_model", {
    enum: ["file", "url", "agreement"],
  }).default("url"),
  uploadedBy: text("uploaded_by", { enum: ["coach", "user"] })
    .notNull()
    .default("coach"),
  externalLink: text("external_link"),
  expirationDate: timestamp("expiration_date", { mode: "date" }),
  publishedAt: timestamp("published_at", { mode: "date" }).defaultNow(),
  ...timestamps,
});

export type EngagementResourceInsert = typeof engagementResources.$inferInsert;
export type EngagementResourceSelect = typeof engagementResources.$inferSelect;

export const assignTagToResources = pgTable(
  "AssignTagToResource",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    resourceId: uuid("resource_id")
      .notNull()
      .references(() => engagementResources.id, { onDelete: "cascade" }),
    tagId: uuid("tag_id")
      .notNull()
      .references(() => tags.id, { onDelete: "cascade" }),
  },
  (table) => ({
    resourceIdIdx: index("assign_tag_to_resource_resource_id_idx").on(
      table.resourceId,
    ),
    tagIdIdx: index("assign_tag_to_resource_tag_id_idx").on(table.tagId),
    resourceTagUnique: uniqueIndex(
      "assign_tag_to_resource_resource_id_tag_id_unique",
    ).on(table.resourceId, table.tagId),
  }),
);

export type AssignTagToResourceInsert =
  typeof assignTagToResources.$inferInsert;
export type AssignTagToResourceSelect =
  typeof assignTagToResources.$inferSelect;

export const fcmTokens = pgTable(
  "fcm_token",
  {
    id: uuid("id").defaultRandom().primaryKey(),
    userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "cascade",
    }),
    token: text("token").notNull().unique(),
    deviceInfo: text("device_info"),
    lastUsed: timestamp("last_used").defaultNow().notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdIdx: index("fcm_token_user_id_idx").on(table.userId),
  }),
);

export type FcmTokenInsert = typeof fcmTokens.$inferInsert;
export type FcmTokenSelect = typeof fcmTokens.$inferSelect;

export const notifications = pgTable(
  "Notification",
  {
    id: uuid("id").defaultRandom().primaryKey(),
    // Recipient user — the account that should see this notification. When
    // recipient="coach" this is the coach's user account (coaches.userId).
    userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
    // Recipient's coach record when recipient="coach"; used for FCM routing and
    // downstream business logic. Not used for card display.
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "cascade",
    }),
    // Actor — who triggered the notification. Used purely for the card's
    // name/avatar. actorCoachId takes precedence over actorUserId when both
    // are set (e.g. a coach who is also a user).
    actorUserId: uuid("actor_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    actorCoachId: uuid("actor_coach_id").references(() => coaches.id, {
      onDelete: "set null",
    }),
    type: notificationTypeEnum("type").notNull(),
    title: text("title").notNull(),
    body: text("body").notNull(),
    isRead: boolean("is_read").default(false).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    relatedEntityType: text("related_entity_type"),
    relatedEntityId: text("related_entity_id"),
    recipient: text("recipient", { enum: ["coach", "user"] })
      .notNull()
      .default("user"),
    link: text("link"),
    data: text("data"),
  },
  (table) => ({
    userIdIdx: index("notification_user_id_idx").on(table.userId),
    coachIdIdx: index("notification_coach_id_idx").on(table.coachId),
  }),
);

export type NotificationInsert = typeof notifications.$inferInsert;
export type NotificationSelect = typeof notifications.$inferSelect;

export type NotificationActor = {
  name: string | null;
  profilePictureUrl: string | null;
} | null;

export type NotificationItem = NotificationSelect & {
  actor: NotificationActor;
};

export const categoriesOfLife = pgTable(
  "CategoryOfLife",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    imageUrl: text("image_url"),
    encryptedName: text("encrypted_name"),
    encryptedShortDescription: text("encrypted_short_description"),
    iv: text("iv"),
    ...timestamps,
  },
  (table) => ({
    userIdIdx: index("category_of_life_user_id_idx").on(table.userId),
  }),
);

export type CategoryOfLifeInsert = typeof categoriesOfLife.$inferInsert;
export type CategoryOfLifeSelect = typeof categoriesOfLife.$inferSelect;

export const overallVisions = pgTable(
  "OverallVision",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    encryptedContent: text("encrypted_content"),
    iv: text("iv"),
    ...timestamps,
  },
  (table) => ({
    userIdIdx: index("overall_vision_user_id_idx").on(table.userId),
  }),
);

export type OverallVisionInsert = typeof overallVisions.$inferInsert;
export type OverallVisionSelect = typeof overallVisions.$inferSelect;

export const bucketListItems = pgTable(
  "BucketListItems",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    encryptedTitle: text("encrypted_title"),
    encryptedDescription: text("encrypted_description"),
    iv: text("iv"),
    images: jsonb("images").$type<string[]>().default([]),
    order: integer("order").notNull().default(0),
    ...timestamps,
  },
  (table) => ({
    userIdIdx: index("bucket_list_user_id_idx").on(table.userId),
  }),
);

export type BucketListInsert = typeof bucketListItems.$inferInsert;
export type BucketListSelect = typeof bucketListItems.$inferSelect;

export const userShared = pgTable(
  "UserShared",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    isSharedOverallViewWithCoach: boolean("is_shared_overall_view_with_coach")
      .default(false)
      .notNull(),
    isSharedGoalsWithCoach: boolean("is_shared_goals_with_coach")
      .default(false)
      .notNull(),
    ...timestamps,
  },
  (table) => ({
    userIdIdx: index("user_shared_user_id_idx").on(table.userId),
  }),
);

export type UserSharedInsert = typeof userShared.$inferInsert;
export type UserSharedSelect = typeof userShared.$inferSelect;

export const transformationStories = pgTable(
  "transformation_stories",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    avatarUrl: text("avatar_url"),
    body: text("body").notNull(),
    source: text("source").notNull(),
    // Platform-wide CIH stories may be linked back to the coach, challenge,
    // and profile testimonial that supplied them. All links are nullable so a
    // published story remains useful when one of those source records goes
    // away.
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "set null",
    }),
    challengeId: uuid("challenge_id").references(() => challenges.id, {
      // Keep the story when a challenge is removed; the challenge is context,
      // not ownership of the platform proof.
      onDelete: "set null",
    }),
    sourceTestimonialId: uuid("source_testimonial_id").references(
      () => testimonials.id,
      { onDelete: "set null" },
    ),
    // Structured source material is optional for legacy summit imports. Nova
    // assembles the public SSS narrative into `body` before submission.
    startingPoint: text("starting_point"),
    whatChanged: text("what_changed"),
    result: text("result"),
    // Consent and provenance are explicit for Nova submissions. Legacy rows
    // keep the published default and have null audit timestamps.
    clientConsent: boolean("client_consent").notNull().default(false),
    clientConsentRecordedAt: timestamp("client_consent_recorded_at", {
      mode: "date",
    }),
    fullNameConsent: boolean("full_name_consent").notNull().default(false),
    coachHonestyAttestedAt: timestamp("coach_honesty_attested_at", {
      mode: "date",
    }),
    outcomeVerified: boolean("outcome_verified").notNull().default(false),
    status: contentStatusEnum("status")
      .notNull()
      // Existing seed/import callers do not pass a status. Their rows are
      // already public stories, so the compatibility default is published.
      .default(ContentStatusEnum.Published),
    reviewedBy: text("reviewed_by"),
    reviewedAt: timestamp("reviewed_at", { mode: "date" }),
    submittedAt: timestamp("submitted_at", { mode: "date" }),
    publishedAt: timestamp("published_at", { mode: "date" }),
    archivedAt: timestamp("archived_at", { mode: "date" }),
    consentWithdrawnAt: timestamp("consent_withdrawn_at", { mode: "date" }),
    version: integer("version").notNull().default(1),
    // Optional link to summit session (for stories that came from summit feedback)
    summitSessionId: uuid("summit_session_id").references(
      () => summitSessions.id,
      { onDelete: "set null" },
    ),
    ...timestamps,
  },
  (table) => [
    index("transformation_stories_source_idx").on(table.source),
    index("transformation_stories_status_idx").on(table.status),
    index("transformation_stories_coach_id_idx").on(table.coachId),
    index("transformation_stories_challenge_id_idx").on(table.challengeId),
    index("transformation_stories_source_testimonial_id_idx").on(
      table.sourceTestimonialId,
    ),
    index("transformation_stories_summit_session_id_idx").on(
      table.summitSessionId,
    ),
  ],
);

export type TransformationStoryInsert =
  typeof transformationStories.$inferInsert;
export type TransformationStorySelect =
  typeof transformationStories.$inferSelect;

// Intake Responses table
export const intakeResponses = pgTable(
  "IntakeResponse",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
    answers: jsonb("answers")
      .notNull()
      .$type<Record<string, string | string[]>>(),
    additionalContext: text("additional_context"),
    aiReflection: text("ai_reflection"),
    recommendedRoute: text("recommended_route").notNull(),
    routingSummary: text("routing_summary"),
    source: text("source"),
    ...timestamps,
  },
  (table) => [
    index("intake_response_user_id_idx").on(table.userId),
    index("intake_response_recommended_route_idx").on(table.recommendedRoute),
  ],
);

export type IntakeResponseInsert = typeof intakeResponses.$inferInsert;

// Coach Fee Exemptions table
export const coachFeeExemptions = pgTable(
  "CoachFeeExemption",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    reason: text("reason").notNull(),
    startDate: timestamp("start_date", { mode: "date" }).notNull().defaultNow(),
    endDate: timestamp("end_date", { mode: "date" }),
    isActive: boolean("is_active").notNull().default(true),
    ...timestamps,
  },
  (table) => [
    index("coach_fee_exemption_coach_id_idx").on(table.coachId),
    index("coach_fee_exemption_is_active_idx").on(table.isActive),
  ],
);

export type CoachFeeExemptionInsert = typeof coachFeeExemptions.$inferInsert;
export type CoachFeeExemptionSelect = typeof coachFeeExemptions.$inferSelect;
export type IntakeResponseSelect = typeof intakeResponses.$inferSelect;

// ---------------------------------------------------------------------------
// Coach Referral Rewards (double-sided credit program — money ledger)
// ---------------------------------------------------------------------------
//
// Append-only-ish ledger for the referrer's $50 account credit. The webhook
// records intent (pending); the daily sweep is the authority that re-validates
// against Stripe before issuing the credit. FKs are `set null` (never cascade):
// a granted row points at a live Stripe credit and must outlive a deleted coach
// (KTD10). Redundant text columns (referrerStripeCustomerId, triggering ids)
// keep the row self-describing after a coach FK is nulled.
export const coachReferralRewards = pgTable(
  "CoachReferralReward",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    referrerCoachId: uuid("referrer_coach_id").references(
      (): AnyPgColumn => coaches.id,
      { onDelete: "set null" },
    ),
    // Full unique — at most one reward per referred coach, forever (KTD8).
    referredCoachId: uuid("referred_coach_id")
      .references((): AnyPgColumn => coaches.id, { onDelete: "set null" })
      .unique(),
    amountCents: integer("amount_cents").notNull(),
    status: coachReferralRewardStatusEnum("status")
      .notNull()
      .default("pending"),
    // "nova" | "membership" — stored as text.
    qualifyingProduct: text("qualifying_product").notNull(),
    triggeringStripeEventId: text("triggering_stripe_event_id"),
    triggeringInvoiceId: text("triggering_invoice_id"),
    triggeringSubscriptionId: text("triggering_subscription_id"),
    triggeringChargeId: text("triggering_charge_id"),
    // Referrer's target customer, locked at insert time (KTD5) — never
    // re-derived at grant time (the coach may have multiple/changed customers).
    referrerStripeCustomerId: text("referrer_stripe_customer_id"),
    // Abuse-review flag (R4) — set later by the first-payment handler (U3).
    paymentMethodFingerprintMatch: boolean("payment_method_fingerprint_match"),
    maturesAt: timestamp("matures_at", { mode: "date" }).notNull(),
    grantedAt: timestamp("granted_at", { mode: "date" }),
    clawedBackAt: timestamp("clawed_back_at", { mode: "date" }),
    stripeBalanceTransactionId: text("stripe_balance_transaction_id"),
    clawbackReason: text("clawback_reason"),
    ...timestamps,
  },
  (table) => [
    // Defense-in-depth backstop for R2 (the real enforcement is the
    // application-layer eligibility check); does not re-evaluate on FK
    // nullification.
    check(
      "coach_referral_reward_no_self_referral",
      sql`${table.referrerCoachId} <> ${table.referredCoachId}`,
    ),
    // Grant sweep + stuck-`granting` recovery scan by (status, maturesAt).
    index("coach_referral_reward_status_matures_at_idx").on(
      table.status,
      table.maturesAt,
    ),
    // Coach-facing credit view + per-referrer rolling-cap query (U2).
    index("coach_referral_reward_referrer_coach_id_idx").on(
      table.referrerCoachId,
    ),
  ],
);

export type CoachReferralRewardInsert =
  typeof coachReferralRewards.$inferInsert;
export type CoachReferralRewardSelect =
  typeof coachReferralRewards.$inferSelect;

// ═══════════════════════════════════════════════════════════════════════════
// ENCRYPTION
// ═══════════════════════════════════════════════════════════════════════════

// User Encryption Keys table - stores user's public/private key pair
export const userEncryptionKeys = pgTable(
  "UserEncryptionKeys",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" })
      .unique(),
    publicKey: text("public_key").notNull(),
    encryptedPrivateKey: text("encrypted_private_key").notNull(),
    recoveryCodeHashes: text("recovery_code_hashes").array().notNull(),
    recoveryEncryptedPrivateKeys: text(
      "recovery_encrypted_private_keys",
    ).array(),
    recoverySalt: text("recovery_salt"),
    salt: text("salt").notNull(),
    failedPinAttempts: integer("failed_pin_attempts").default(0),
    lockedUntil: timestamp("locked_until", { mode: "date" }),
    failedRecoveryAttempts: integer("failed_recovery_attempts").default(0),
    ...timestamps,
  },
  (table) => ({
    userIdIdx: index("user_encryption_keys_user_id_idx").on(table.userId),
  }),
);

export type UserEncryptionKeysInsert = typeof userEncryptionKeys.$inferInsert;
export type UserEncryptionKeysSelect = typeof userEncryptionKeys.$inferSelect;

// Key Grants table - stores DEK (Data Encryption Key) grants for access control
export const keyGrants = pgTable(
  "KeyGrants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    resourceType: varchar("resource_type", { length: 50 }).notNull(),
    resourceId: uuid("resource_id").notNull(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    encryptedDek: text("encrypted_dek").notNull(),
    ...timestamps,
  },
  (table) => ({
    resourceTypeIdx: index("key_grants_resource_type_idx").on(
      table.resourceType,
    ),
    userIdIdx: index("key_grants_user_id_idx").on(table.userId),
    uniqueResourceUser: uniqueIndex("key_grants_unique_resource_user").on(
      table.resourceType,
      table.resourceId,
      table.userId,
    ),
  }),
);

export type KeyGrantsInsert = typeof keyGrants.$inferInsert;
export type KeyGrantsSelect = typeof keyGrants.$inferSelect;

// ---------------------------------------------------------------------------
// Feature Flags
// ---------------------------------------------------------------------------

export const featureFlags = pgTable("FeatureFlag", {
  id: uuid("id").primaryKey().defaultRandom(),
  key: text("key").notNull().unique(),
  description: text("description"),
  enabled: boolean("enabled").notNull().default(false),
  allUsers: boolean("all_users").notNull().default(false),
  whitelistedUserIds: uuid("whitelisted_user_ids").array().default([]),
  ...timestamps,
});

export type FeatureFlagInsert = typeof featureFlags.$inferInsert;
export type FeatureFlagSelect = typeof featureFlags.$inferSelect;

// ---------------------------------------------------------------------------
// Admin settings
// ---------------------------------------------------------------------------

/**
 * What an admin saved for a setting that `ADMIN_SETTINGS`
 * (`src/lib/shared/admin-settings.ts`) declares. A setting without a row uses
 * its default from code. Each value is checked against its setting's schema
 * when it is written and again when it is read.
 */
export const adminSettings = pgTable("admin_settings", {
  key: text().primaryKey(),
  value: jsonb().$type<JsonValue>().notNull(),
  updatedByUserId: uuid("updated_by_user_id").references(() => users.id, {
    onDelete: "set null",
  }),
  ...timestamps,
});

// ---------------------------------------------------------------------------
// Program templates
// ---------------------------------------------------------------------------

/**
 * Reusable program blueprint: define a package once and pre-fill the
 * create-program page from it. Its homework is copied into each new program
 * as `ScheduledHomework`, so later edits never reach existing programs.
 */
export const programTemplates = pgTable(
  "ProgramTemplate",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    sessionCount: integer("session_count").notNull(),
    cadence: text("cadence", { enum: ["weekly", "biweekly"] })
      .notNull()
      .default("weekly"),
    /** Weekdays 0 (Sun) – 6 (Sat) the sessions fall on; null = the program's start date. */
    weekdays: integer("weekdays").array(),
    sessionDurationMinutes: integer("session_duration_minutes")
      .notNull()
      .default(60),
    /** Local "HH:mm" the sessions start, in the coach's time zone when a program starts; null = no preset time. */
    sessionTime: text("session_time"),
    /** Title per session number (index 0 = session 1); "" = no title; null = none. */
    sessionTitles: text("session_titles").array(),
    meetingLink: text("meeting_link"),
    askInitialGoals: boolean("ask_initial_goals").notNull().default(false),
    /** Invoices the program sends, counted from its start date; empty = free program. */
    instalments: jsonb("instalments")
      .$type<ProgramTemplateInstalment[]>()
      .notNull()
      .default([]),
    currency: text("currency").notNull().default("USD"),
    /** Homework each program gets after the session with that number ends. */
    homework: jsonb("homework")
      .$type<ProgramTemplateHomework[]>()
      .notNull()
      .default([]),
    ...timestamps,
  },
  (table) => ({
    coachIdx: index("program_template_coach_idx").on(table.coachId),
  }),
);

export type ProgramTemplateInsert = typeof programTemplates.$inferInsert;
export type ProgramTemplateSelect = typeof programTemplates.$inferSelect;

/**
 * A link, file or agreement a template puts in each new program's Resources.
 * A file is stored once under the template's own key
 * (`buildTemplateResourceKey`); each program gets its own copy of the object,
 * so deleting a program resource or the template never touches the other.
 */
export const programTemplateResources = pgTable(
  "ProgramTemplateResource",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    templateId: uuid("template_id")
      .notNull()
      .references(() => programTemplates.id, { onDelete: "cascade" }),
    /** Owner of the template; part of the stored file's key. */
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    /** Order in the template's list. */
    position: integer("position").notNull(),
    title: text("title").notNull(),
    uploadModel: text("upload_model", {
      enum: ["file", "url", "agreement"],
    }).notNull(),
    /** Set for a link. */
    externalLink: text("external_link"),
    /** Set for a file or an agreement. */
    filename: text("filename"),
    ...timestamps,
  },
  (table) => ({
    templateIdx: index("program_template_resource_template_idx").on(
      table.templateId,
    ),
  }),
);

export type ProgramTemplateResourceSelect =
  typeof programTemplateResources.$inferSelect;

/**
 * Template homework copied into a program and not given to the client yet.
 * The 15-minute job turns a row into a homework task once the program's
 * `sessionNumber`th non-cancelled session has ended, and deletes the row in
 * the same transaction, so each row becomes at most one task. Rows of a
 * program that ends first are deleted unsent.
 */
export const scheduledHomework = pgTable(
  "ScheduledHomework",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    /** 1 = the program's first non-cancelled session by start time. */
    sessionNumber: integer("session_number").notNull(),
    /** Order within the session, as in the template. */
    position: integer("position").notNull(),
    title: text("title").notNull(),
    ...timestamps,
  },
  (table) => ({
    engagementIdx: index("scheduled_homework_engagement_idx").on(
      table.engagementId,
    ),
  }),
);

export type ScheduledHomeworkSelect = typeof scheduledHomework.$inferSelect;

// ---------------------------------------------------------------------------
// Nova E2E sharing ("Nova as a person" model)
// ---------------------------------------------------------------------------

/**
 * Per-engagement coach opt-in to share E2E-encrypted engagement data with
 * Nova. Sharing re-wraps resource DEKs for Nova's platform-held keypair
 * (see ~/lib/server/nova-identity), so shared data is server-decryptable by
 * design — opt-in, revocable (grants deleted on disable), and audited via
 * NovaAccessLog.
 */
export const novaEngagementShares = pgTable(
  "NovaEngagementShare",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    enabled: boolean("enabled").notNull().default(true),
    ...timestamps,
  },
  (table) => ({
    engagementUnique: uniqueIndex("nova_engagement_share_engagement_unique").on(
      table.engagementId,
    ),
    coachIdx: index("nova_engagement_share_coach_idx").on(table.coachId),
  }),
);

export type NovaEngagementShareInsert =
  typeof novaEngagementShares.$inferInsert;
export type NovaEngagementShareSelect =
  typeof novaEngagementShares.$inferSelect;

/** Every Nova decrypt of shared engagement data — surfaced to the coach. */
export const novaAccessLogs = pgTable(
  "NovaAccessLog",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    resourceType: varchar("resource_type", { length: 50 }).notNull(),
    resourceId: uuid("resource_id").notNull(),
    /** What Nova was doing, e.g. "supervisor_briefing". */
    purpose: text("purpose").notNull(),
    ...timestamps,
  },
  (table) => ({
    engagementIdx: index("nova_access_log_engagement_idx").on(
      table.engagementId,
      table.createdAt,
    ),
    coachIdx: index("nova_access_log_coach_idx").on(
      table.coachId,
      table.createdAt,
    ),
  }),
);

export type NovaAccessLogInsert = typeof novaAccessLogs.$inferInsert;
export type NovaAccessLogSelect = typeof novaAccessLogs.$inferSelect;

// ---------------------------------------------------------------------------
// MCP Audit Log
// ---------------------------------------------------------------------------

export const mcpAuditLog = pgTable(
  "mcp_audit_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: text("user_id").notNull(),
    tool: text("tool").notNull(),
    entityType: text("entity_type"), // "cco" | "ctp" | "framework" | null
    entityId: uuid("entity_id"),
    inputSummary: jsonb("input_summary"),
    outcome: text("outcome").notNull(), // "success" | "error"
    errorCode: text("error_code"),
    durationMs: integer("duration_ms"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index("mcp_audit_log_user_id_idx").on(table.userId),
    index("mcp_audit_log_created_at_idx").on(table.createdAt),
    index("mcp_audit_log_tool_idx").on(table.tool),
  ],
);

export type McpAuditLogInsert = typeof mcpAuditLog.$inferInsert;
export type McpAuditLogSelect = typeof mcpAuditLog.$inferSelect;

// ---------------------------------------------------------------------------
// Nova AI Engine
// ---------------------------------------------------------------------------

export const novaAgentType = pgEnum("nova_agent_type", [
  "diagnose",
  "plan",
  "assets",
  "grow",
]);

export const novaConversationStatus = pgEnum("nova_conversation_status", [
  "active",
  "paused",
  "completed",
  "abandoned",
]);

export const novaMessageRole = pgEnum("nova_message_role", [
  "user",
  "assistant",
  "system",
  "tool",
]);

export const novaDeliverableStatus = pgEnum("nova_deliverable_status", [
  "draft",
  "review",
  "locked",
  "revised",
]);

export const novaDeliverableApplicationStatus = pgEnum(
  "nova_deliverable_application_status",
  ["applying", "applied"],
);

export const novaQueueItemKind = pgEnum("nova_queue_item_kind", [
  "strategy_deliverable",
  "voice_profile",
  "profile_update",
  "linkedin_profile",
  "cih_article",
  "linkedin_post",
  "outreach_script",
  "mini_sprint",
  "video_scripts",
  "mini_sprint_video_script",
  "article_proposals",
  "coach_story",
  "wheel_page_copy",
]);

export const novaQueueItemStatus = pgEnum("nova_queue_item_status", [
  "pending",
  "generating",
  "ready_for_review",
  "approved",
  "rejected",
  "failed",
]);

export const novaRunStatus = pgEnum("nova_run_status", [
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
  "aborted_token_cap",
  "blocked_quota",
]);

export const novaTurnStatus = pgEnum("nova_turn_status", [
  "accepted",
  "short_active",
  "queued",
  "running",
  "finalizing",
  "completed",
  "failed",
  "quota_blocked",
  "cancelled",
]);

export const novaTurnExecutionMode = pgEnum("nova_turn_execution_mode", [
  "short",
  "long",
]);

export const novaPrcprAssessmentState = pgEnum("nova_prcpr_assessment_state", [
  "draft",
  "claimed",
]);
export const novaPrcprAssessmentResultPhase = pgEnum(
  "nova_prcpr_assessment_result_phase",
  ["public_provisional", "map_ready", "monthly_rerun", "correction"],
);

export const novaTurnReservationStatus = pgEnum(
  "nova_turn_reservation_status",
  ["reserved", "consumed", "released"],
);

export const novaConversations = pgTable(
  "nova_conversations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    agentType: novaAgentType("agent_type").notNull(),
    status: novaConversationStatus("status").notNull().default("active"),
    title: text("title"),
    currentPhase: integer("current_phase").notNull().default(0),
    metadata: jsonb("metadata").$type<Record<string, JsonValue>>(),
    extractedData: jsonb("extracted_data").$type<Record<string, JsonValue>>(),
    // Orthogonal to `status`: a thread of any status can be archived (swept out
    // of active surfaces). NULL = live. See Studio interaction-model spec §4.
    archivedAt: timestamp("archived_at", { mode: "date" }),
    // The chat turn currently allowed to persist its output, stamped before
    // the model runs (see stampActiveTurnId). Dedicated columns — NOT metadata
    // keys — because updateConversation does whole-object metadata replaces,
    // which would clobber the stamp mid-turn and fail the supersede guard open.
    activeTurnId: uuid("active_turn_id"),
    activeTurnIsRegen: boolean("active_turn_is_regen"),
    totalInputTokens: integer("total_input_tokens").notNull().default(0),
    totalOutputTokens: integer("total_output_tokens").notNull().default(0),
    ...timestamps,
  },
  (table) => ({
    coachIdIdx: index("nova_conversations_coach_id_idx").on(table.coachId),
    statusIdx: index("nova_conversations_status_idx").on(table.status),
    archivedAtIdx: index("nova_conversations_archived_at_idx").on(
      table.archivedAt,
    ),
  }),
);

export type NovaConversationInsert = typeof novaConversations.$inferInsert;
export type NovaConversationSelect = typeof novaConversations.$inferSelect;

export const novaMessageSequence = pgSequence("nova_messages_sequence_seq");

export const novaMessages = pgTable(
  "nova_messages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sequence: bigint("sequence", { mode: "number" }).default(
      sql`nextval('nova_messages_sequence_seq')`,
    ),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => novaConversations.id, { onDelete: "cascade" }),
    role: novaMessageRole("role").notNull(),
    content: text("content"),
    parts: jsonb("parts").$type<JsonValue[]>(),
    phase: integer("phase"),
    inputTokens: integer("input_tokens"),
    outputTokens: integer("output_tokens"),
    // Set when a regeneration replaced this row's turn output (see
    // supersedeMessagesAfter). NULL = live. Superseded rows are kept for
    // audit but excluded from loadMessages (model history + UI hydration).
    supersededAt: timestamp("superseded_at", {
      withTimezone: true,
      mode: "date",
    }),
    ...timestamps,
  },
  (table) => ({
    conversationIdIdx: index("nova_messages_conversation_id_idx").on(
      table.conversationId,
    ),
    createdAtIdx: index("nova_messages_created_at_idx").on(table.createdAt),
  }),
);

export type NovaMessageInsert = typeof novaMessages.$inferInsert;
export type NovaMessageSelect = typeof novaMessages.$inferSelect;

/**
 * One durable, client-identified NOVA request. A transport retry must resolve
 * this row instead of creating another user message or execution path.
 */
export const novaTurns = pgTable(
  "nova_turns",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => novaConversations.id, { onDelete: "cascade" }),
    clientTurnId: uuid("client_turn_id").notNull(),
    requestDigest: varchar("request_digest", { length: 64 }).notNull(),
    requestSnapshot: jsonb("request_snapshot")
      .$type<Record<string, JsonValue>>()
      .notNull(),
    testScenario: jsonb("test_scenario").$type<NovaTestScenarioDescriptor>(),
    userMessageId: uuid("user_message_id")
      .notNull()
      .references(() => novaMessages.id, { onDelete: "cascade" }),
    status: novaTurnStatus("status").notNull().default("accepted"),
    executionMode: novaTurnExecutionMode("execution_mode"),
    runId: uuid("run_id").references((): AnyPgColumn => novaRuns.id, {
      onDelete: "set null",
    }),
    resultMessageId: uuid("result_message_id").references(
      (): AnyPgColumn => novaMessages.id,
      { onDelete: "set null" },
    ),
    regeneratePredecessorId: uuid("regenerate_predecessor_id").references(
      (): AnyPgColumn => novaTurns.id,
      { onDelete: "set null" },
    ),
    publicErrorCode: text("public_error_code"),
    terminalAt: timestamp("terminal_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    clientTurnIdUniqueIdx: uniqueIndex(
      "nova_turns_client_turn_id_unique_idx",
    ).on(table.clientTurnId),
    userMessageIdUniqueIdx: uniqueIndex(
      "nova_turns_user_message_id_unique_idx",
    ).on(table.userMessageId),
    coachConversationCreatedAtIdx: index(
      "nova_turns_coach_conversation_created_at_idx",
    ).on(table.coachId, table.conversationId, table.createdAt),
    coachStatusIdx: index("nova_turns_coach_status_idx").on(
      table.coachId,
      table.status,
    ),
    // The stale-turn reaper sweeps globally (no coach_id), so coachStatusIdx
    // cannot serve it. Partial on the non-terminal statuses: the table grows a
    // row per chat send forever, but live turns are a handful at any instant,
    // so this stays tiny and turns the sweep's seq scan into a range scan.
    activeUpdatedAtIdx: index("nova_turns_active_updated_at_idx")
      .on(table.updatedAt)
      .where(
        sql`${table.status} in ('accepted', 'short_active', 'queued', 'running', 'finalizing')`,
      ),
    runIdUniqueIdx: uniqueIndex("nova_turns_run_id_unique_idx").on(table.runId),
  }),
);

export type NovaTurnInsert = typeof novaTurns.$inferInsert;
export type NovaTurnSelect = typeof novaTurns.$inferSelect;

/** Stop receipt accepted before the matching turn admission commits. */
export const novaTurnCancellationIntents = pgTable(
  "nova_turn_cancellation_intents",
  {
    clientTurnId: uuid("client_turn_id").primaryKey(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => novaConversations.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => ({
    coachConversationIdx: index(
      "nova_turn_cancellation_intents_coach_conversation_idx",
    ).on(table.coachId, table.conversationId),
  }),
);

export type NovaTurnCancellationIntentInsert =
  typeof novaTurnCancellationIntents.$inferInsert;
export type NovaTurnCancellationIntentSelect =
  typeof novaTurnCancellationIntents.$inferSelect;

/** One idempotent capacity/quota reservation owned by a durable turn. */
export const novaTurnReservations = pgTable(
  "nova_turn_reservations",
  {
    turnId: uuid("turn_id")
      .primaryKey()
      .references(() => novaTurns.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    usageDate: text("usage_date").notNull(),
    status: novaTurnReservationStatus("status").notNull(),
    quotaEnforced: boolean("quota_enforced").notNull(),
    maxActiveTurns: integer("max_active_turns"),
    dailyInputTokenLimit: integer("daily_input_token_limit"),
    dailyOutputTokenLimit: integer("daily_output_token_limit"),
    dailyCostUsdLimit: doublePrecision("daily_cost_usd_limit"),
    reservedInputTokens: integer("reserved_input_tokens").notNull().default(0),
    reservedOutputTokens: integer("reserved_output_tokens")
      .notNull()
      .default(0),
    reservedCostUsd: doublePrecision("reserved_cost_usd").notNull().default(0),
    limitReason: text("limit_reason"),
    settledAt: timestamp("settled_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    coachStatusIdx: index("nova_turn_reservations_coach_status_idx").on(
      table.coachId,
      table.status,
    ),
  }),
);

export type NovaTurnReservationInsert =
  typeof novaTurnReservations.$inferInsert;
export type NovaTurnReservationSelect =
  typeof novaTurnReservations.$inferSelect;

/** Model-prepared writes materialized only by the short-turn terminal winner. */
export const novaTurnEffects = pgTable(
  "nova_turn_effects",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    turnId: uuid("turn_id")
      .notNull()
      .references(() => novaTurns.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    effectKey: text("effect_key").notNull(),
    effect: jsonb("effect").$type<NovaTurnEffect>().notNull(),
    appliedAt: timestamp("applied_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    turnEffectKeyUniqueIdx: uniqueIndex(
      "nova_turn_effects_turn_effect_key_unique_idx",
    ).on(table.turnId, table.effectKey),
    turnCreatedAtIdx: index("nova_turn_effects_turn_created_at_idx").on(
      table.turnId,
      table.createdAt,
    ),
  }),
);

export type NovaTurnEffectInsert = typeof novaTurnEffects.$inferInsert;
export type NovaTurnEffectSelect = typeof novaTurnEffects.$inferSelect;

/**
 * Owner-scoped append-only projection log. Durable state is committed first;
 * broadcasts and cache invalidation replay this ordered record after commit.
 */
export const novaWorkspaceChanges = pgTable(
  "nova_workspace_changes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    sequence: integer("sequence").notNull(),
    eventKey: text("event_key").notNull(),
    type: text("type").notNull(),
    resourceKind: text("resource_kind").notNull(),
    resourceId: uuid("resource_id").notNull(),
    data: jsonb("data").$type<Record<string, JsonValue>>().notNull(),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => ({
    coachSequenceUniqueIdx: uniqueIndex(
      "nova_workspace_changes_coach_sequence_unique_idx",
    ).on(table.coachId, table.sequence),
    coachEventKeyUniqueIdx: uniqueIndex(
      "nova_workspace_changes_coach_event_key_unique_idx",
    ).on(table.coachId, table.eventKey),
    coachCreatedAtIdx: index("nova_workspace_changes_coach_created_at_idx").on(
      table.coachId,
      table.createdAt,
    ),
  }),
);

export type NovaWorkspaceChangeInsert =
  typeof novaWorkspaceChanges.$inferInsert;
export type NovaWorkspaceChangeSelect =
  typeof novaWorkspaceChanges.$inferSelect;

export const novaAiUsageEvents = pgTable(
  "nova_ai_usage_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    conversationId: uuid("conversation_id").references(
      () => novaConversations.id,
      { onDelete: "cascade" },
    ),
    runId: uuid("run_id").references((): AnyPgColumn => novaRuns.id, {
      onDelete: "cascade",
    }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    requestId: text("request_id"),
    agentType: novaAgentType("agent_type").notNull(),
    currentSkill: text("current_skill"),
    runIntent: text("run_intent"),
    runStepKey: text("run_step_key"),
    model: text("model").notNull(),
    purpose: text("purpose").notNull(),
    inputTokens: integer("input_tokens").notNull().default(0),
    cacheReadInputTokens: integer("cache_read_input_tokens")
      .notNull()
      .default(0),
    cacheWriteInputTokens: integer("cache_write_input_tokens")
      .notNull()
      .default(0),
    outputTokens: integer("output_tokens").notNull().default(0),
    ...timestamps,
  },
  (table) => ({
    conversationIdIdx: index("nova_ai_usage_events_conversation_id_idx").on(
      table.conversationId,
    ),
    runIdIdx: index("nova_ai_usage_events_run_id_idx").on(table.runId),
    coachIdIdx: index("nova_ai_usage_events_coach_id_idx").on(table.coachId),
    coachIdCreatedAtIdx: index(
      "nova_ai_usage_events_coach_id_created_at_idx",
    ).on(table.coachId, table.createdAt),
    createdAtIdx: index("nova_ai_usage_events_created_at_idx").on(
      table.createdAt,
    ),
    attachedCheck: check(
      "nova_ai_usage_events_attached_check",
      sql`${table.conversationId} IS NOT NULL OR ${table.runId} IS NOT NULL`,
    ),
  }),
);

export type NovaAiUsageEventInsert = typeof novaAiUsageEvents.$inferInsert;
export type NovaAiUsageEventSelect = typeof novaAiUsageEvents.$inferSelect;

// The one analytics event store: funnel events and NOVA usage, server-side
// (client GTM/GA4 stays for client-side interactions). One writer function
// (`recordEvent`) inserts idempotently on `occurrenceKey`; events the catalog
// marks `ghlTag` fan out to GHL as a best-effort push, and `ghlPushedAt` tracks
// that delivery for the daily sweep's retry. `userId` is nullable because
// webhook-subject events may hit legacy coaches with no linked users row.
export const events = pgTable(
  "events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "cascade",
    }),
    event: text("event").notNull(),
    lane: text("lane").notNull().default("self_serve"),
    occurrenceKey: text("occurrence_key").notNull(),
    payload: jsonb("payload").$type<Record<string, JsonValue>>(),
    ghlPushedAt: timestamp("ghl_pushed_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    occurrenceUnique: unique("events_occurrence_unique")
      .on(table.event, table.userId, table.coachId, table.occurrenceKey)
      .nullsNotDistinct(),
    coachIdCreatedAtIdx: index("events_coach_id_created_at_idx").on(
      table.coachId,
      table.createdAt,
    ),
    unpushedIdx: index("events_unpushed_idx")
      .on(table.coachId, table.createdAt)
      .where(sql`${table.ghlPushedAt} IS NULL`),
    eventCoachIdx: index("events_event_coach_idx").on(
      table.event,
      table.coachId,
    ),
  }),
);

/**
 * Stable artifact families. Draft candidates remain visible without replacing
 * the approved head used by generic NOVA prompts and workspace summaries.
 */
export const novaDeliverableLineages = pgTable(
  "nova_deliverable_lineages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    type: text("type").notNull(),
    approvedHeadId: uuid("approved_head_id").references(
      (): AnyPgColumn => novaDeliverables.id,
      { onDelete: "set null" },
    ),
    ...timestamps,
  },
  (table) => ({
    coachTypeIdx: index("nova_deliverable_lineages_coach_type_idx").on(
      table.coachId,
      table.type,
    ),
  }),
);

export type NovaDeliverableLineageInsert =
  typeof novaDeliverableLineages.$inferInsert;
export type NovaDeliverableLineageSelect =
  typeof novaDeliverableLineages.$inferSelect;

export const novaDeliverables = pgTable(
  "nova_deliverables",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Nullable: deliverables are the durable artifacts and must SURVIVE their
    // originating thread. Deleting a conversation detaches this (set null),
    // never cascades — a coach tidying old chats must not lose their ICP.
    conversationId: uuid("conversation_id").references(
      () => novaConversations.id,
      { onDelete: "set null" },
    ),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    type: text("type").notNull(),
    status: novaDeliverableStatus("status").notNull().default("draft"),
    version: integer("version").notNull().default(1),
    lineageId: uuid("lineage_id").references(() => novaDeliverableLineages.id, {
      onDelete: "restrict",
    }),
    predecessorId: uuid("predecessor_id").references(
      (): AnyPgColumn => novaDeliverables.id,
      { onDelete: "restrict" },
    ),
    lineageVersion: integer("lineage_version"),
    producerTurnId: uuid("producer_turn_id").references(() => novaTurns.id, {
      onDelete: "set null",
    }),
    // Who authored this revision's content; "coach" means hand-edited.
    authoredBy: text("authored_by")
      .$type<"nova" | "coach">()
      .notNull()
      .default("nova"),
    data: jsonb("data").$type<Record<string, JsonValue>>().notNull(),
    lockedAt: timestamp("locked_at", { mode: "date" }),
    // Confirmation pointer for FOUNDATION deliverables (icp/offer/positioning).
    // Set from queue approval to the row's `version` at approval time; a later
    // content revision bumps `version` past it, so `confirmedVersion < version`
    // reads as "refined since confirmed". Null until first confirmed. This is
    // the live confirmation signal — the `status` enum is inert (always draft).
    confirmedVersion: integer("confirmed_version"),
    confirmedAt: timestamp("confirmed_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    coachIdIdx: index("nova_deliverables_coach_id_idx").on(table.coachId),
    conversationTypeIdx: index("nova_deliverables_conversation_type_idx").on(
      table.conversationId,
      table.type,
    ),
    lineageVersionUniqueIdx: uniqueIndex(
      "nova_deliverables_lineage_version_unique_idx",
    ).on(table.lineageId, table.lineageVersion),
    producerTurnUniqueIdx: uniqueIndex(
      "nova_deliverables_producer_turn_unique_idx",
    ).on(table.producerTurnId),
    predecessorNotSelfCheck: check(
      "nova_deliverables_predecessor_not_self_check",
      sql`${table.predecessorId} IS NULL OR ${table.predecessorId} <> ${table.id}`,
    ),
  }),
);

export type NovaDeliverableInsert = typeof novaDeliverables.$inferInsert;
export type NovaDeliverableSelect = typeof novaDeliverables.$inferSelect;

/**
 * Deliverables in retired formats (offer_structure, positioning_statement,
 * positioning_summary, business_diagnosis), parked by the retire-legacy-nova-data
 * migration instead of deleted. This table is their permanent read-only store;
 * nothing writes to it. The coach reads her latest output of each type on Past
 * conversations, and the Plan agent reads the offer and positioning ones as the
 * coach's earlier thinking.
 *
 * Aliases: retired deliverables, parked nova work, legacy deliverable holding table
 */
export const novaRetiredDeliverables = pgTable(
  "nova_retired_deliverables",
  {
    /** The original `nova_deliverables.id`, kept so a row traces back to its source. */
    id: uuid("id").primaryKey(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    type: text("type").notNull(),
    // Text, not the live status enum: this read-only store must not pin a column
    // the live schema is free to change.
    status: text("status").notNull(),
    version: integer("version").notNull(),
    authoredBy: text("authored_by").notNull(),
    data: jsonb("data").$type<Record<string, JsonValue>>().notNull(),
    createdAt: timestamp("created_at", { mode: "date" }).notNull(),
    updatedAt: timestamp("updated_at", { mode: "date" }).notNull(),
    retiredAt: timestamp("retired_at", { mode: "date" }).notNull().defaultNow(),
  },
  (table) => ({
    coachTypeIdx: index("nova_retired_deliverables_coach_type_idx").on(
      table.coachId,
      table.type,
    ),
  }),
);

export type NovaRetiredDeliverableInsert =
  typeof novaRetiredDeliverables.$inferInsert;
export type NovaRetiredDeliverableSelect =
  typeof novaRetiredDeliverables.$inferSelect;

/** Queued with a completed map; email delivery must survive request failures. */
export const businessMapEmails = pgTable(
  "business_map_emails",
  {
    deliverableId: uuid("deliverable_id")
      .primaryKey()
      .references(() => novaDeliverables.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    isUpdate: boolean("is_update").notNull(),
    createdAt: timestamp("created_at", { mode: "date" }).notNull().defaultNow(),
    attemptedAt: timestamp("attempted_at", { mode: "date" }),
    sentAt: timestamp("sent_at", { mode: "date" }),
  },
  (table) => ({
    pendingIdx: index("business_map_emails_pending_idx")
      .on(table.attemptedAt, table.createdAt)
      .where(sql`${table.sentAt} IS NULL`),
  }),
);

/** One durable, retryable application claim for an exact locked revision. */
export const novaDeliverableApplications = pgTable(
  "nova_deliverable_applications",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    deliverableId: uuid("deliverable_id")
      .notNull()
      .references(() => novaDeliverables.id, { onDelete: "restrict" }),
    requestDigest: varchar("request_digest", { length: 64 }).notNull(),
    status: novaDeliverableApplicationStatus("status")
      .notNull()
      .default("applying"),
    result: jsonb("result").$type<Record<string, JsonValue>>(),
    appliedAt: timestamp("applied_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    deliverableUniqueIdx: uniqueIndex(
      "nova_deliverable_applications_deliverable_unique_idx",
    ).on(table.deliverableId),
    coachCreatedAtIdx: index(
      "nova_deliverable_applications_coach_created_at_idx",
    ).on(table.coachId, table.createdAt),
  }),
);

export type NovaDeliverableApplicationInsert =
  typeof novaDeliverableApplications.$inferInsert;
export type NovaDeliverableApplicationSelect =
  typeof novaDeliverableApplications.$inferSelect;

/**
 * Retired: the PRcPR Business Health Score, replaced by the 6-Figure Readiness
 * Score (`readiness_assessments`). Parked, not dropped, because the rows are
 * coaches' own answers: nothing writes them, and the only reader tells a coach
 * her earlier score used different questions (`hasRetiredBusinessHealthScore`)
 * and deletes them with her account. Drop both tables and those readers
 * together (grep "retired").
 */
export const novaPrcprAssessments = pgTable(
  "nova_prcpr_assessments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    state: novaPrcprAssessmentState("state").notNull().default("draft"),
    /** Authenticated retries can resolve a retired public draft to its owner. */
    canonicalAssessmentId: uuid("canonical_assessment_id").references(
      (): AnyPgColumn => novaPrcprAssessments.id,
      { onDelete: "cascade" },
    ),
    /** Cleared on claim so the anonymous credential cannot authorize later. */
    claimTokenHash: text("claim_token_hash").unique(),
    claimExpiresAt: timestamp("claim_expires_at", { mode: "date" }).notNull(),
    userId: uuid("user_id").references(() => users.id, {
      onDelete: "cascade",
    }),
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "set null",
    }),
    claimedAt: timestamp("claimed_at", { mode: "date" }),
    /** Records the public aggregate-use disclosure acknowledged before intake. */
    aggregateUseDisclosureAcknowledgedAt: timestamp(
      "aggregate_use_disclosure_acknowledged_at",
      { mode: "date" },
    ),
    /**
     * Her own decision about anonymized aggregate use, deliberately separate
     * from the acknowledgement above: that timestamp only records that the
     * disclosure was shown. Tri-state on purpose. `null` means the choice was
     * never put to her (rows written before the checkbox existed, or by a flow
     * that does not ask); `false` means she was asked and declined. Only `true`
     * may enter aggregate work, which keeps the enforcing predicate a plain
     * `= true` and stops a legacy null from reading as agreement.
     */
    aggregateUseConsent: boolean("aggregate_use_consent"),
    /**
     * When the value above was last set, so a consent can be demonstrated and a
     * later withdrawal stays distinguishable. Null exactly when consent is null.
     */
    aggregateUseConsentRecordedAt: timestamp(
      "aggregate_use_consent_recorded_at",
      { mode: "date" },
    ),
    /** Cleared by a fresh full-map snapshot; guards one monthly reminder per cycle. */
    lastRerunReminderAt: timestamp("last_rerun_reminder_at", {
      mode: "date",
    }),
    /** Short lease for a reminder being delivered; cleared after send settles. */
    rerunReminderClaimedAt: timestamp("rerun_reminder_claimed_at", {
      mode: "date",
    }),
    /** Snapshot cycle held by the short delivery lease. */
    rerunReminderClaimedSnapshotId: uuid("rerun_reminder_claimed_snapshot_id"),
    configVersion: text("config_version").notNull(),
    rawAnswers: jsonb("raw_answers")
      .$type<Record<string, JsonValue>>()
      .notNull()
      .default({}),
    /** Preserve measurement intent when the coach resumes an unfinished update. */
    draftOperation: text("draft_operation").$type<"correction" | "monthly">(),
    /** Optimistic concurrency token for draft edits and a future claim flow. */
    revision: integer("revision").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "date" }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "date" })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (table) => ({
    claimExpiresAtIdx: index("nova_prcpr_assessments_claim_expires_at_idx").on(
      table.claimExpiresAt,
    ),
    canonicalAssessmentIdx: index(
      "nova_prcpr_assessments_canonical_assessment_idx",
    ).on(table.canonicalAssessmentId),
    userCreatedAtIdx: index("nova_prcpr_assessments_user_created_at_idx").on(
      table.userId,
      table.createdAt,
    ),
    coachCreatedAtIdx: index("nova_prcpr_assessments_coach_created_at_idx").on(
      table.coachId,
      table.createdAt,
    ),
  }),
);

export type NovaPrcprAssessmentInsert =
  typeof novaPrcprAssessments.$inferInsert;
export type NovaPrcprAssessmentSelect =
  typeof novaPrcprAssessments.$inferSelect;

/** Retired with `novaPrcprAssessments`: its append-only result snapshots. */
export const novaPrcprAssessmentSnapshots = pgTable(
  "nova_prcpr_assessment_snapshots",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    assessmentId: uuid("assessment_id")
      .notNull()
      .references(() => novaPrcprAssessments.id, { onDelete: "cascade" }),
    idempotencyKey: text("idempotency_key").notNull(),
    resultPhase: novaPrcprAssessmentResultPhase("result_phase").notNull(),
    /** Effective full snapshot this measurement or correction was based on. */
    sourceSnapshotId: uuid("source_snapshot_id").references(
      (): AnyPgColumn => novaPrcprAssessmentSnapshots.id,
    ),
    /** Original command revision, retained for exact idempotent replay. */
    expectedRevision: integer("expected_revision"),
    configVersion: text("config_version").notNull(),
    rawAnswers: jsonb("raw_answers")
      .$type<Record<string, JsonValue>>()
      .notNull(),
    result: jsonb("result").$type<Record<string, JsonValue>>().notNull(),
    /** Draft revision observed when this immutable snapshot was appended. */
    assessmentRevision: integer("assessment_revision").notNull(),
    createdAt: timestamp("created_at", { mode: "date" }).notNull().defaultNow(),
  },
  (table) => ({
    assessmentIdempotencyUniqueIdx: uniqueIndex(
      "nova_prcpr_assessment_snapshots_idempotency_unique_idx",
    ).on(table.assessmentId, table.idempotencyKey),
    assessmentPhaseCreatedAtIdx: index(
      "nova_prcpr_assessment_snapshots_assessment_phase_created_at_idx",
    ).on(table.assessmentId, table.resultPhase, table.createdAt),
  }),
);

export type NovaPrcprAssessmentSnapshotInsert =
  typeof novaPrcprAssessmentSnapshots.$inferInsert;
export type NovaPrcprAssessmentSnapshotSelect =
  typeof novaPrcprAssessmentSnapshots.$inferSelect;

export const readinessAssessmentSurface = pgEnum(
  "readiness_assessment_surface",
  ["public", "signed_in"],
);

/**
 * One attempt at the 6-Figure Readiness Score. The row is created when the
 * attempt starts and completed once, when the answers are submitted; answers
 * stay in the browser until then, so a completed row never changes except for
 * its claim and its reminder marker.
 *
 * A public attempt has no owner until the emailed link claims it. Until then it
 * is reached only through two hashed credentials: the browser cookie token and
 * the emailed claim-link token. The raw tokens never enter the database.
 */
export const readinessAssessments = pgTable(
  "readiness_assessments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").references(() => users.id, {
      onDelete: "cascade",
    }),
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "set null",
    }),
    surface: readinessAssessmentSurface("surface").notNull(),
    configVersion: text("config_version").notNull(),
    answers: jsonb("answers").$type<ReadinessAnswers>(),
    result: jsonb("result").$type<ReadinessResult>(),
    completedAt: timestamp("completed_at", { mode: "date" }),
    /** SHA-256 of the browser cookie token. Cleared on claim. */
    accessTokenHash: text("access_token_hash").unique(),
    /**
     * SHA-256 of the emailed link token. Kept after claim until `expiresAt`,
     * because mail scanners open links before people do and a second click
     * must still sign her in.
     */
    claimTokenHash: text("claim_token_hash").unique(),
    /** Unclaimed attempts are deleted after this; the claim link stops working. */
    expiresAt: timestamp("expires_at", { mode: "date" }),
    /** What she typed on the email step, kept only until the claim. */
    contactFirstName: text("contact_first_name"),
    contactEmail: text("contact_email"),
    /**
     * The referral code in the browser that typed the email. A claim that
     * creates her account credits it; the claim request's own cookie never
     * counts, because a mail scanner can make that request.
     */
    referralCode: text("referral_code"),
    claimedAt: timestamp("claimed_at", { mode: "date" }),
    /**
     * The claim link created her account, so she still has to pick a
     * password. Later clicks read it too, because a mail scanner can make the
     * first one.
     */
    claimCreatedAccount: boolean("claim_created_account")
      .notNull()
      .default(false),
    /**
     * Set once the provider accepts the result email. Until then the claim
     * link may never have reached her, so a sweep issues and sends a new one.
     */
    resultEmailSentAt: timestamp("result_email_sent_at", { mode: "date" }),
    /** Set once the provider accepts the monthly rerun reminder. */
    rerunReminderSentAt: timestamp("rerun_reminder_sent_at", {
      mode: "date",
    }),
    /**
     * A sweep's lease on sending the rerun reminder. It expires, so a sweep
     * that dies mid-send leaves the reminder to the next one.
     */
    rerunReminderClaimedAt: timestamp("rerun_reminder_claimed_at", {
      mode: "date",
    }),
    ...timestamps,
  },
  (table) => ({
    userCompletedAtIdx: index("readiness_assessments_user_completed_at_idx").on(
      table.userId,
      table.completedAt,
    ),
    coachCompletedAtIdx: index(
      "readiness_assessments_coach_completed_at_idx",
    ).on(table.coachId, table.completedAt),
    expiresAtIdx: index("readiness_assessments_expires_at_idx").on(
      table.expiresAt,
    ),
    ownerOrCredentialCheck: check(
      "readiness_assessments_owner_or_credential_check",
      sql`"user_id" IS NOT NULL OR ("access_token_hash" IS NOT NULL AND "expires_at" IS NOT NULL)`,
    ),
    completedCheck: check(
      "readiness_assessments_completed_check",
      sql`("completed_at" IS NULL) = ("result" IS NULL) AND ("result" IS NULL) = ("answers" IS NULL)`,
    ),
  }),
);

/**
 * Emailed links that open the finish-account page for one account
 * (`/api/account/link`). Only the token's SHA-256 is stored. The cron
 * deletes a row once it expires; before that a finished account's link opens
 * nothing and goes straight on.
 */
export const accountLinks = pgTable(
  "account_links",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    /** The address the link was sent to: the one finishing keeps. */
    email: text("email").notNull(),
    tokenHash: text("token_hash").notNull().unique(),
    createdAt: timestamp("created_at", { mode: "date", withTimezone: true })
      .defaultNow()
      .notNull(),
    expiresAt: timestamp("expires_at", {
      mode: "date",
      withTimezone: true,
    }).notNull(),
  },
  (table) => [
    index("account_links_user_id_idx").on(table.userId),
    index("account_links_expires_at_idx").on(table.expiresAt),
  ],
);

/**
 * The browser that started a signed-out score trial, bound to the account it
 * made. Its httpOnly cookie holds a nonce whose SHA-256 is `nonce_hash`. It lets
 * that browser reopen Checkout and, once `paid_at` is set, reach the finish
 * page's code step. It never signs anyone in. `readiness_assessment_id` is the
 * attempt this browser took: the only score the handoff can read, and the one
 * claimed once she has paid.
 */
export const publicTrialHandoffs = pgTable(
  "public_trial_handoffs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    nonceHash: text("nonce_hash").notNull().unique(),
    readinessAssessmentId: uuid("readiness_assessment_id").references(
      () => readinessAssessments.id,
      { onDelete: "set null" },
    ),
    paidAt: timestamp("paid_at", { mode: "date", withTimezone: true }),
    createdAt: timestamp("created_at", { mode: "date", withTimezone: true })
      .defaultNow()
      .notNull(),
    expiresAt: timestamp("expires_at", {
      mode: "date",
      withTimezone: true,
    }).notNull(),
  },
  (table) => [
    index("public_trial_handoffs_created_at_idx").on(table.createdAt),
    index("public_trial_handoffs_expires_at_idx").on(table.expiresAt),
  ],
);

export type ReadinessAssessmentInsert =
  typeof readinessAssessments.$inferInsert;
export type ReadinessAssessmentSelect =
  typeof readinessAssessments.$inferSelect;

export const novaHooks = pgTable(
  "nova_hooks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    // The deliverable that generated this hook (the Mini Sprint). Kept for lineage.
    sourceDeliverableId: uuid("source_deliverable_id").references(
      () => novaDeliverables.id,
      { onDelete: "set null" },
    ),
    // The offer this hook was generated for, when materialized. Enables attribution.
    offerId: uuid("offer_id").references(() => offers.id, {
      onDelete: "set null",
    }),
    text: text("text").notNull(),
    // The seam the hook works (e.g. "entry_point" | "trigger_moment" | "psychographic"), derived from the generation structure.
    angle: text("angle"),
    // The mechanism the line pulls with ("frustration" | "readiness" | "curiosity_gap" | "mirror" | "identity_gap" | "speed_binary" | "question_form") — see HOOK_TAGS. Independent of `angle`; null on rows written before the taxonomy.
    tag: text("tag"),
    // Which surface it was made for: "mini_sprint" (cannibalization on), "core_offer" (off), or "content" (sells nothing directly).
    surface: text("surface").notNull(),
    // Where the hook is in its life: "draft" | "approved" | "retired". Defaults to approved because `save_hooks` only runs once the coach has approved the bank.
    status: text("status").notNull().default("approved"),
    // Channels the line actually reads well in — any of "social" | "email" | "dm". Null on rows written before the taxonomy.
    channelFit: text("channel_fit").array(),
    // Quality score from the measured loop — null until conversion data exists (nothing is "proven" without data).
    score: integer("score"),
    ...timestamps,
  },
  (table) => ({
    coachIdIdx: index("nova_hooks_coach_id_idx").on(table.coachId),
    // The common read is "this coach's live hooks" — list_hooks excludes retired rows by default.
    coachIdStatusIdx: index("nova_hooks_coach_id_status_idx").on(
      table.coachId,
      table.status,
    ),
    sourceDeliverableIdIdx: index("nova_hooks_source_deliverable_id_idx").on(
      table.sourceDeliverableId,
    ),
  }),
);

export type NovaHookInsert = typeof novaHooks.$inferInsert;
export type NovaHookSelect = typeof novaHooks.$inferSelect;

export const novaRuns = pgTable(
  "nova_runs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    conversationId: uuid("conversation_id").references(
      () => novaConversations.id,
      { onDelete: "set null" },
    ),
    intent: text("intent").notNull(),
    input: jsonb("input").$type<Record<string, JsonValue>>().notNull(),
    trigger: jsonb("trigger")
      .$type<{
        kind: "user" | "auto";
        source: string;
        metadata?: Record<string, JsonValue>;
      }>()
      .notNull(),
    status: novaRunStatus("status").notNull().default("queued"),
    workflowInstanceId: text("workflow_instance_id"),
    result: jsonb("result").$type<Record<string, JsonValue>>(),
    error: jsonb("error").$type<{
      code: string;
      message: string;
      stepKey?: string;
      stack?: string;
    }>(),
    parentRunId: uuid("parent_run_id").references(
      (): AnyPgColumn => novaRuns.id,
      {
        onDelete: "set null",
      },
    ),
    totalInputTokens: integer("total_input_tokens").notNull().default(0),
    totalOutputTokens: integer("total_output_tokens").notNull().default(0),
    totalCacheReadInputTokens: integer("total_cache_read_input_tokens")
      .notNull()
      .default(0),
    totalCacheWriteInputTokens: integer("total_cache_write_input_tokens")
      .notNull()
      .default(0),
    estimatedCostUsd: doublePrecision("estimated_cost_usd")
      .notNull()
      .default(0),
    startedAt: timestamp("started_at", { mode: "date" }),
    completedAt: timestamp("completed_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    coachIdStatusIdx: index("nova_runs_coach_id_status_idx").on(
      table.coachId,
      table.status,
    ),
    coachIdCreatedAtIdx: index("nova_runs_coach_id_created_at_idx").on(
      table.coachId,
      table.createdAt,
    ),
    conversationIdIdx: index("nova_runs_conversation_id_idx").on(
      table.conversationId,
    ),
    parentRunIdIdx: index("nova_runs_parent_run_id_idx").on(table.parentRunId),
  }),
);

export type NovaRunInsert = typeof novaRuns.$inferInsert;
export type NovaRunSelect = typeof novaRuns.$inferSelect;

export const novaRunEvents = pgTable(
  "nova_run_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    runId: uuid("run_id")
      .notNull()
      .references(() => novaRuns.id, { onDelete: "cascade" }),
    sequence: integer("sequence").notNull(),
    type: text("type").notNull(),
    stepKey: text("step_key"),
    data: jsonb("data").$type<Record<string, JsonValue>>().notNull(),
    tokenUsage: jsonb("token_usage").$type<{
      inputTokens: number;
      outputTokens: number;
      cacheReadInputTokens?: number;
      cacheWriteInputTokens?: number;
    }>(),
    createdAt: timestamp("created_at", { mode: "date" }).notNull().defaultNow(),
  },
  (table) => ({
    runIdSequenceUniqueIdx: uniqueIndex(
      "nova_run_events_run_id_sequence_unique_idx",
    ).on(table.runId, table.sequence),
    runIdCreatedAtIdx: index("nova_run_events_run_id_created_at_idx").on(
      table.runId,
      table.createdAt,
    ),
  }),
);

export type NovaRunEventInsert = typeof novaRunEvents.$inferInsert;
export type NovaRunEventSelect = typeof novaRunEvents.$inferSelect;

export const novaCoachDailyUsage = pgTable(
  "nova_coach_daily_usage",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    usageDate: text("usage_date").notNull(), // ISO YYYY-MM-DD (UTC); text avoids tz pitfalls
    inputTokens: integer("input_tokens").notNull().default(0),
    outputTokens: integer("output_tokens").notNull().default(0),
    cacheReadInputTokens: integer("cache_read_input_tokens")
      .notNull()
      .default(0),
    cacheWriteInputTokens: integer("cache_write_input_tokens")
      .notNull()
      .default(0),
    estimatedCostUsd: doublePrecision("estimated_cost_usd")
      .notNull()
      .default(0),
    chatEventCount: integer("chat_event_count").notNull().default(0),
    runEventCount: integer("run_event_count").notNull().default(0),
    ...timestamps,
  },
  (table) => ({
    pk: primaryKey({ columns: [table.coachId, table.usageDate] }),
    usageDateIdx: index("nova_coach_daily_usage_usage_date_idx").on(
      table.usageDate,
    ),
  }),
);

export type NovaCoachDailyUsageInsert = typeof novaCoachDailyUsage.$inferInsert;
export type NovaCoachDailyUsageSelect = typeof novaCoachDailyUsage.$inferSelect;

/**
 * Unused since the routines engine (#1128) replaced it; nothing reads or
 * writes it. Kept so this release's migration does not drop it while the
 * previous Worker, which still reads it, serves traffic. A follow-up drops it
 * once this code is live.
 */
export const novaTriggers = pgTable(
  "nova_triggers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(),
    enabled: boolean("enabled").notNull().default(true),
    config: jsonb("config")
      .$type<Record<string, JsonValue>>()
      .notNull()
      .default({}),
    lastFiredAt: timestamp("last_fired_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => ({
    coachIdKindUniqueIdx: uniqueIndex(
      "nova_triggers_coach_id_kind_unique_idx",
    ).on(table.coachId, table.kind),
    coachIdEnabledIdx: index("nova_triggers_coach_id_enabled_idx").on(
      table.coachId,
      table.enabled,
    ),
  }),
);

export const novaRoutineStatus = pgEnum(
  "nova_routine_status",
  NOVA_ROUTINE_STATUSES,
);

export const novaRoutineRunStatus = pgEnum(
  "nova_routine_run_status",
  NOVA_ROUTINE_RUN_STATUSES,
);

/**
 * One NOVA routine per coach and kind (`NOVA_ROUTINE_KINDS`): a weekly
 * schedule on her own clock and whether it runs. The schedule is read in her
 * user's time zone (`User.time_zone`, New York until she saves one), and
 * `next_run_at` is when the runner next opens a run. Only the runner and her
 * own routine server functions write it.
 */
export const novaRoutines = pgTable(
  "nova_routines",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    kind: text("kind").$type<NovaRoutineKind>().notNull(),
    status: novaRoutineStatus("status").notNull().default("on"),
    /** 0 is Sunday, as `Date.getDay()` counts. */
    weekday: integer("weekday").notNull(),
    /** `HH:MM`, 24-hour, on her clock. */
    localTime: text("local_time").notNull(),
    nextRunAt: timestamp("next_run_at", { mode: "date" }).notNull(),
    /** When it last stopped running, by her or after two missed runs. */
    pausedAt: timestamp("paused_at", { mode: "date" }),
    /**
     * When it last started counting: created, turned back on, or eligible
     * again after a lapse. Only runs due since then count toward the two
     * missed runs that pause it.
     */
    activeSince: timestamp("active_since", { mode: "date" })
      .notNull()
      .defaultNow(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("nova_routines_coach_id_kind_unique_idx").on(
      table.coachId,
      table.kind,
    ),
    index("nova_routines_next_run_at_on_idx")
      .on(table.nextRunAt)
      .where(sql`${table.status} = 'on'`),
    check(
      "nova_routines_weekday_range",
      sql`${table.weekday} BETWEEN 0 AND 6`,
    ),
    check(
      "nova_routines_local_time_format",
      sql`${table.localTime} ~ '^([01][0-9]|2[0-3]):[0-5][0-9]$'`,
    ),
  ],
);

export type NovaRoutineInsert = typeof novaRoutines.$inferInsert;
export type NovaRoutineSelect = typeof novaRoutines.$inferSelect;

/**
 * One run of a routine: open from `due_at` until she completes it or the
 * next run opens, when an open run becomes missed. `result` holds what the
 * run recorded, in the shape its kind parses (for the weekly check-in: her
 * numbers, last week's move, this week's move and her win). Unique per
 * routine and due time, so a runner that runs twice opens one run.
 */
export const novaRoutineRuns = pgTable(
  "nova_routine_runs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    routineId: uuid("routine_id")
      .notNull()
      .references(() => novaRoutines.id, { onDelete: "cascade" }),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    dueAt: timestamp("due_at", { mode: "date" }).notNull(),
    status: novaRoutineRunStatus("status").notNull().default("open"),
    completedAt: timestamp("completed_at", { mode: "date" }),
    result: jsonb("result").$type<Record<string, JsonValue>>(),
    /** The NOVA run behind this run's suggestion, when one was started. */
    novaRunId: uuid("nova_run_id").references(() => novaRuns.id, {
      onDelete: "set null",
    }),
    emailSentAt: timestamp("email_sent_at", { mode: "date" }),
    reminderSentAt: timestamp("reminder_sent_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("nova_routine_runs_routine_id_due_at_unique_idx").on(
      table.routineId,
      table.dueAt,
    ),
    index("nova_routine_runs_coach_id_due_at_idx").on(
      table.coachId,
      table.dueAt,
    ),
  ],
);

export type NovaRoutineRunInsert = typeof novaRoutineRuns.$inferInsert;
export type NovaRoutineRunSelect = typeof novaRoutineRuns.$inferSelect;

/**
 * Nova queue items — the unified review surface for everything Nova produces.
 *
 * Polymorphic via `(refTable, refId)`: each row points at the typed table
 * (e.g. `nova_deliverables`) holding the actual content. `resolveQueueItem`
 * in `src/lib/server/nova/queue.ts` is the single read path that dispatches
 * on refTable, keeping the set of valid refTable values small and explicit.
 *
 * `dependsOn` is reserved for tiered dependency chains (sub-agents that
 * require upstream queue items to be approved first). Shipped as a column
 * now to avoid a later migration; left null for v1.
 */
export const novaQueueItems = pgTable(
  "nova_queue_items",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    kind: novaQueueItemKind("kind").notNull(),
    refTable: text("ref_table").notNull(),
    refId: uuid("ref_id").notNull(),
    tier: integer("tier").notNull().default(0),
    status: novaQueueItemStatus("status").notNull().default("pending"),
    dependsOn: jsonb("depends_on").$type<string[]>(),
    generatedBy: text("generated_by"),
    generatedAt: timestamp("generated_at", { mode: "date" }),
    reviewedAt: timestamp("reviewed_at", { mode: "date" }),
    reviewNotes: text("review_notes"),
    metadata: jsonb("metadata").$type<Record<string, JsonValue>>(),
    ...timestamps,
  },
  (table) => ({
    coachStatusIdx: index("nova_queue_items_coach_status_idx").on(
      table.coachId,
      table.status,
    ),
    coachKindIdx: index("nova_queue_items_coach_kind_idx").on(
      table.coachId,
      table.kind,
    ),
    // Unique on (refTable, refId): each artifact should have at most one
    // queue row. Enables atomic upsert via onConflictDoUpdate in enqueueItem.
    refUniqueIdx: uniqueIndex("nova_queue_items_ref_unique_idx").on(
      table.refTable,
      table.refId,
    ),
  }),
);

export type NovaQueueItemInsert = typeof novaQueueItems.$inferInsert;
export type NovaQueueItemSelect = typeof novaQueueItems.$inferSelect;

// ═══════════════════════════════════════════════════════════════════════════
// CIH — CHALLENGES (L1 pillar pages)
// ═══════════════════════════════════════════════════════════════════════════

export const challenges = pgTable(
  "challenges",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: text("slug"),
    title: text("title").notNull(),
    subtitle: text("subtitle"),
    description: text("description").notNull(),
    body: jsonb("body").$type<JsonValue>(),
    heroImageUrl: text("hero_image_url"),
    tagline: text("tagline"),
    icon: text("icon"),
    faqs: jsonb("faqs").$type<{ question: string; answer: string }[]>(),
    reviewerName: text("reviewer_name"),
    reviewerCredentials: text("reviewer_credentials"),
    reviewedDate: text("reviewed_date"),
    intakeTrigger: text("intake_trigger"),
    draftOf: uuid("draft_of").references((): AnyPgColumn => challenges.id, {
      onDelete: "cascade",
    }),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("challenges_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("challenges_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("challenges_status_idx").on(table.status),
  ],
);

export type ChallengeInsert = typeof challenges.$inferInsert;
export type ChallengeSelect = typeof challenges.$inferSelect;

// ═══════════════════════════════════════════════════════════════════════════
// CIH — FRAMEWORKS (V1 methodology/standard pages)
// ═══════════════════════════════════════════════════════════════════════════

export const frameworks = pgTable(
  "frameworks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: text("slug"),
    name: text("name").notNull(),
    type: frameworkTypeEnum("type")
      .notNull()
      .default(FrameworkTypeEnum.Framework),
    description: text("description").notNull(),
    logoUrl: text("logo_url"),
    body: jsonb("body").$type<JsonValue>(),
    ccoId: uuid("cco_id").references(() => ccos.id, { onDelete: "set null" }),
    narrativeStats: jsonb("narrative_stats").$type<NarrativeStat[]>(),
    hidePlatformStats: boolean("hide_platform_stats").notNull().default(false),
    pullQuote: text("pull_quote"),
    pullQuoteAttribution: text("pull_quote_attribution"),
    faqs: jsonb("faqs").$type<{ question: string; answer: string }[]>(),
    draftOf: uuid("draft_of").references((): AnyPgColumn => frameworks.id, {
      onDelete: "cascade",
    }),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("frameworks_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("frameworks_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("frameworks_status_idx").on(table.status),
    index("frameworks_type_idx").on(table.type),
    index("frameworks_cco_idx").on(table.ccoId),
  ],
);

export type FrameworkInsert = typeof frameworks.$inferInsert;
export type FrameworkSelect = typeof frameworks.$inferSelect;

// ═══════════════════════════════════════════════════════════════════════════
// CIH — COACHING ARTICLES (L2 coach-specific pages)
// ═══════════════════════════════════════════════════════════════════════════

export const coachingArticles = pgTable(
  "coaching_articles",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: text("slug").notNull(),
    title: text("title").notNull(),
    description: text("description").notNull(),
    body: jsonb("body").$type<JsonValue>(),
    // Either coachId (coach-authored) or authorName (admin free-text author) is
    // set, never both — enforced by coaching_articles_author_xor_check below.
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "cascade",
    }),
    authorName: text("author_name"),
    authorTitle: text("author_title"),
    challengeId: uuid("challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "restrict" }),
    draftOf: uuid("draft_of").references(
      (): AnyPgColumn => coachingArticles.id,
      { onDelete: "cascade" },
    ),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("coaching_articles_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("ca_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("ca_coach_idx").on(table.coachId),
    index("ca_challenge_idx").on(table.challengeId),
    index("ca_status_idx").on(table.status),
    check(
      "coaching_articles_author_xor_check",
      sql`(${table.coachId} IS NOT NULL AND ${table.authorName} IS NULL) OR (${table.coachId} IS NULL AND ${table.authorName} IS NOT NULL)`,
    ),
  ],
);

export type CoachingArticleInsert = typeof coachingArticles.$inferInsert;
export type CoachingArticleSelect = typeof coachingArticles.$inferSelect;

// CIH — Join tables

export const challengeFrameworks = pgTable(
  "challenge_frameworks",
  {
    challengeId: uuid("challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "cascade" }),
    frameworkId: uuid("framework_id")
      .notNull()
      .references(() => frameworks.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.challengeId, table.frameworkId] }),
    index("cf_challenge_idx").on(table.challengeId),
    index("cf_framework_idx").on(table.frameworkId),
  ],
);

export const ctpFrameworks = pgTable(
  "ctp_frameworks",
  {
    ctpId: uuid("ctp_id")
      .notNull()
      .references(() => ctps.id, { onDelete: "cascade" }),
    frameworkId: uuid("framework_id")
      .notNull()
      .references(() => frameworks.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.ctpId, table.frameworkId] }),
    index("ctpf_ctp_idx").on(table.ctpId),
    index("ctpf_framework_idx").on(table.frameworkId),
  ],
);

/**
 * Which organizations accredit a training program.
 *
 * `ctps.ccoId` is a different relationship: the single school that *delivers*
 * the program. Accreditation is separate and many-to-many — a program is
 * delivered by one school but can carry the endorsement of several bodies
 * (ICF, EMCC, AC...), and a body accredits programs it does not deliver.
 * Without this table an accreditation body's page could only ever show the
 * programs it ran itself, which for a pure accreditor is none.
 *
 * A program is never linked to its own delivering school here — `ctps.ccoId`
 * already says that, and recording it twice would double-count the school's
 * own programs on its page. `attachCtpAccreditor` enforces it.
 */
export const ctpAccreditations = pgTable(
  "ctp_accreditations",
  {
    ctpId: uuid("ctp_id")
      .notNull()
      .references(() => ctps.id, { onDelete: "cascade" }),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.ctpId, table.ccoId] }),
    index("ctpa_ctp_idx").on(table.ctpId),
    index("ctpa_cco_idx").on(table.ccoId),
  ],
);

export type CtpAccreditationInsert = typeof ctpAccreditations.$inferInsert;
export type CtpAccreditationSelect = typeof ctpAccreditations.$inferSelect;

export const challengeRelated = pgTable(
  "challenge_related",
  {
    challengeId: uuid("challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "cascade" }),
    relatedChallengeId: uuid("related_challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.challengeId, table.relatedChallengeId] }),
    index("cr_challenge_idx").on(table.challengeId),
    index("cr_related_idx").on(table.relatedChallengeId),
  ],
);

// Join table for coaches and frameworks (coach claims expertise in a framework)
export const coachesFrameworks = pgTable(
  "coaches_frameworks",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    frameworkId: uuid("framework_id")
      .notNull()
      .references(() => frameworks.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.frameworkId] }),
    index("coach_fw_coach_idx").on(table.coachId),
    index("coach_fw_framework_idx").on(table.frameworkId),
  ],
);

export type CoachFrameworkInsert = typeof coachesFrameworks.$inferInsert;
export type CoachFrameworkSelect = typeof coachesFrameworks.$inferSelect;

// Direct coach <-> challenge tagging. The authoritative source for both
// the public profile's challenge tile grid and the challenge page's
// coach listing. Replaces two prior derived paths:
//   * coachesFrameworks -> challengeFrameworks -> challenges  (profile side)
//   * challenges -> challengesExpertiseCategories ->
//       expertiseSubcategories -> coachExpertise -> coaches    (challenge side)
// Both inferences left coaches without matching frameworks / expertise
// invisible. Direct tagging gives every coach a way to claim the
// challenges they help with.
export const coachesChallenges = pgTable(
  "coaches_challenges",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    challengeId: uuid("challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.challengeId] }),
    index("coach_ch_coach_idx").on(table.coachId),
    index("coach_ch_challenge_idx").on(table.challengeId),
  ],
);

export type CoachChallengeInsert = typeof coachesChallenges.$inferInsert;
export type CoachChallengeSelect = typeof coachesChallenges.$inferSelect;

// Join table linking challenges to expertise categories (coaches found via their existing expertises)
export const challengesExpertiseCategories = pgTable(
  "challenges_expertise_categories",
  {
    challengeId: uuid("challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "cascade" }),
    expertiseCategoryId: uuid("expertise_category_id")
      .notNull()
      .references(() => expertiseCategories.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.challengeId, table.expertiseCategoryId] }),
    index("ch_ec_challenge_idx").on(table.challengeId),
    index("ch_ec_category_idx").on(table.expertiseCategoryId),
  ],
);

export type ChallengeExpertiseCategoryInsert =
  typeof challengesExpertiseCategories.$inferInsert;
export type ChallengeExpertiseCategorySelect =
  typeof challengesExpertiseCategories.$inferSelect;

// ═══════════════════════════════════════════════════════════════════════════
// CIH V3 — COACH BUSINESS BUILDER VERTICAL
// Guides (/coaching-business/[slug]) + Playbooks (/coaching-business/
// playbooks/[slug]) + Thought Leaders (author reference, no public page).
// Coach-facing counterpart to V1/V2. Cross-linking is editorial — no joins.
// ═══════════════════════════════════════════════════════════════════════════

// Thought leaders — normalized author entity behind playbooks. Reference data
// only: no draft/publish lifecycle, no public page of its own in V3.0.
export const thoughtLeaders = pgTable(
  "thought_leaders",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    slug: text("slug"),
    organization: text("organization"),
    website: text("website"),
    ipSensitivity: ipSensitivityEnum("ip_sensitivity")
      .notNull()
      .default(IpSensitivityEnum.Low),
    partnerTier: partnerTierEnum("partner_tier"),
    cautiousFlag: boolean("cautious_flag").notNull().default(false),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("thought_leaders_slug_unique")
      .on(table.slug)
      .where(sql`${table.slug} IS NOT NULL`),
    index("thought_leaders_name_idx").on(table.name),
  ],
);

export type ThoughtLeaderInsert = typeof thoughtLeaders.$inferInsert;
export type ThoughtLeaderSelect = typeof thoughtLeaders.$inferSelect;

/**
 * PrcPR taxonomy — the shared classification carried by every guide and
 * playbook, owned by the Knowledge Layer Mapping sheet rather than by editors.
 *
 * All nullable: the sheet signs rows off in waves, so an unclassified row is a
 * real and expected state, not a default to be faked. Backfilled by
 * `src/scripts/backfill-cih-prcpr-taxonomy.ts`.
 *
 * Drafts carry these columns too: `createDraftOfPublished` seeds them from the
 * canonical row and `promoteDraft` copies them back, so the round trip is
 * symmetric and editing taxonomy on a draft takes effect on publish.
 */
export const prcprTaxonomyColumns = {
  /** Lowest annual band the page is written for ("floor"). */
  practiceStageBand: practiceStageBandEnum("practice_stage_band"),
  /** Band whose CBB offer the page's CTA should route to. */
  ctaStageBand: practiceStageBandEnum("cta_stage_band"),
  /** The single chain link this page primarily serves. */
  prcprPrimary: prcprLinkEnum("prcpr_primary"),
  /** Every chain link the page touches, primary first. */
  prcprLinks: prcprLinkEnum("prcpr_links").array(),
  /**
   * Editorial category ("Marketing", "Finance", …). Plain text, not an enum:
   * the sheet ships this as an explicitly unverified first-pass taxonomy.
   */
  category: text("category"),
  /** When this row's taxonomy was last reconciled with the mapping sheet. */
  lastTaxonomyReview: timestamp("last_taxonomy_review", {
    withTimezone: true,
    mode: "date",
  }),
  /**
   * The index rail this page calls home. Shared across guides and playbooks
   * on purpose — `mom-test-coaches` is a playbook that lives on the Starting
   * Out shelf, so the vocabulary can't be scoped to one table.
   */
  shelf: libraryShelfEnum("shelf"),
  /**
   * Sort position within that shelf, ascending; null for rows the sheet has
   * not sequenced yet. Deliberately not a unique slot like `startHereOrder` —
   * a shelf is a rail of up to two dozen items, not a fixed set of exclusive
   * positions, so no uniqueness constraint is enforced here.
   */
  curriculumOrder: integer("curriculum_order"),
  /** The reading sequence this page belongs to. Null for most pages. */
  seriesFamily: seriesFamilyEnum("series_family"),
  /**
   * Position within that series, 1-based. The series total is deliberately
   * never stored: the sheet's "N/M" has M equal to the member count today,
   * but a stored M starts lying the moment a member is unpublished — derive
   * "N of M" from the count of currently published members instead.
   */
  seriesPosition: integer("series_position"),
} as const;

// Guides — coach-facing how-to content. One template, tagged by type and
// practice stage; practice/cta stage drive CBB CTA routing and filtering.
export const guides = pgTable(
  "guides",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: text("slug"),
    title: text("title").notNull(),
    description: text("description").notNull(),
    metaTitle: text("meta_title"),
    body: jsonb("body").$type<JsonValue>(),
    heroImageUrl: text("hero_image_url"),
    guideType: guideTypeEnum("guide_type")
      .notNull()
      .default(GuideTypeEnum.Tool),
    practiceStage: practiceStageEnum("practice_stage")
      .notNull()
      .default(PracticeStageEnum.All),
    /**
     * Retired: no feature reads or sets it. The closing CBB box reads
     * `ctaStageBand`. Dropped by its own migration once the code that stopped
     * using it is deployed.
     */
    ctaStage: practiceStageEnum("cta_stage")
      .notNull()
      .default(PracticeStageEnum.All),
    ...prcprTaxonomyColumns,
    faqs: jsonb("faqs").$type<{ question: string; answer: string }[]>(),
    pullQuote: text("pull_quote"),
    pullQuoteAttribution: text("pull_quote_attribution"),
    reviewerName: text("reviewer_name"),
    reviewerCredentials: text("reviewer_credentials"),
    reviewedDate: text("reviewed_date"),
    draftOf: uuid("draft_of").references((): AnyPgColumn => guides.id, {
      onDelete: "cascade",
    }),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("guides_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("guides_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("guides_status_idx").on(table.status),
    index("guides_type_idx").on(table.guideType),
    index("guides_practice_stage_idx").on(table.practiceStage),
    index("guides_shelf_idx").on(table.shelf),
  ],
);

export type GuideInsert = typeof guides.$inferInsert;
export type GuideSelect = typeof guides.$inferSelect;

// Playbooks — named business/marketing frameworks (Hormozi, Brunson, …)
// written through the coaching-practice lens. Authored by a thought leader.
export const playbooks = pgTable(
  "playbooks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: text("slug"),
    title: text("title").notNull(),
    thoughtLeaderId: uuid("thought_leader_id")
      .notNull()
      .references(() => thoughtLeaders.id, { onDelete: "restrict" }),
    description: text("description").notNull(),
    body: jsonb("body").$type<JsonValue>(),
    /**
     * Retired: no feature reads or sets it. The closing CBB box reads
     * `ctaStageBand`. Dropped by its own migration once the code that stopped
     * using it is deployed.
     */
    ctaStage: practiceStageEnum("cta_stage")
      .notNull()
      .default(PracticeStageEnum.Starting),
    ...prcprTaxonomyColumns,
    /**
     * Position on the six-playbook "Start Here" shelf, 1–6. Null for every
     * other playbook — the shelf is a hand-picked six, not a ranking of all.
     */
    startHereOrder: integer("start_here_order"),
    faqs: jsonb("faqs").$type<{ question: string; answer: string }[]>(),
    pullQuote: text("pull_quote"),
    pullQuoteAttribution: text("pull_quote_attribution"),
    reviewerName: text("reviewer_name"),
    reviewerCredentials: text("reviewer_credentials"),
    reviewedDate: text("reviewed_date"),
    draftOf: uuid("draft_of").references((): AnyPgColumn => playbooks.id, {
      onDelete: "cascade",
    }),
    ...contentStatusColumns,
    ...timestamps,
  },
  (table) => [
    uniqueIndex("playbooks_slug_unique")
      .on(table.slug)
      .where(sql`${table.draftOf} IS NULL`),
    uniqueIndex("playbooks_draft_of_unique")
      .on(table.draftOf)
      .where(sql`${table.draftOf} IS NOT NULL`),
    index("playbooks_status_idx").on(table.status),
    index("playbooks_thought_leader_idx").on(table.thoughtLeaderId),
    index("playbooks_shelf_idx").on(table.shelf),
    // The Start Here shelf is six ordered slots: one playbook per position,
    // counted over canonical rows only so a draft copy can hold the same slot.
    uniqueIndex("playbooks_start_here_order_unique")
      .on(table.startHereOrder)
      .where(sql`${table.draftOf} IS NULL`),
    check(
      "playbooks_start_here_order_range",
      sql`${table.startHereOrder} IS NULL OR ${table.startHereOrder} BETWEEN 1 AND 6`,
    ),
  ],
);

export type PlaybookInsert = typeof playbooks.$inferInsert;
export type PlaybookSelect = typeof playbooks.$inferSelect;

export const coachAssessmentTemplateEnum = pgEnum("coach_assessment_template", [
  "wheel_of_life",
]);

/**
 * A coach's switch for one assessment template on their own public page
 * (`/{username}/wheel-of-life`). Not a quiz definition: questions and scoring
 * stay in the template's code. `labels` holds the coach's own eight
 * categories; null means the template's standard ones.
 */
export const coachAssessments = pgTable(
  "coach_assessments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    template: coachAssessmentTemplateEnum("template").notNull(),
    isActive: boolean("is_active").notNull().default(false),
    activatedAt: timestamp("activated_at", { mode: "date" }),
    labels: jsonb("labels").$type<string[]>(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("coach_assessments_coach_template_unique").on(
      table.coachId,
      table.template,
    ),
  ],
);

export const wheelOfLifeSessions = pgTable(
  "WheelOfLifeSession",
  {
    id: text("id").primaryKey(), // short slug (nanoid, 10 chars)
    userId: uuid("user_id"), // nullable — anonymous first
    // The coach whose page the session was taken on; null on the platform page.
    coachId: uuid("coach_id").references(() => coaches.id, {
      onDelete: "set null",
    }),
    labels: jsonb("labels").notNull().$type<string[]>(), // 8 strings
    scores: jsonb("scores").notNull().$type<number[]>(), // 8 ints 1-10
    reflection: text("reflection"), // AI output, null until generated
    suggestions: jsonb("suggestions").$type<
      {
        categoryIndex: number;
        cihItemId: string;
        cihItemType: "article" | "framework" | "challenge" | "ctp" | "cco";
        score: number;
      }[]
    >(), // null until generated
    source: text("source"), // utm-style entry point
    ...timestamps,
  },
  (table) => [
    index("wol_user_id_idx").on(table.userId),
    index("wol_coach_id_idx").on(table.coachId),
  ],
);

export type WheelOfLifeSessionInsert = typeof wheelOfLifeSessions.$inferInsert;
export type WheelOfLifeSessionSelect = typeof wheelOfLifeSessions.$inferSelect;

/** Where a lead stands with the coach. Set by the coach, never by DCM. */
export const assessmentLeadStatusEnum = pgEnum("assessment_lead_status", [
  "new",
  "contacted",
  "call_booked",
  "became_client",
  "not_a_fit",
]);

/**
 * A visitor who shared their details on a coach's assessment page. One row
 * per coach and email: a second visit adds an attempt. It is the coach's
 * lead, apart from Contacts, until the visitor saves their results through
 * the emailed link.
 */
export const assessmentLeads = pgTable(
  "assessment_leads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    /** Lowercased at the boundary. */
    email: text("email").notNull(),
    firstName: text("first_name").notNull(),
    /** Set by the claim link; cleared when the visitor deletes their account. */
    userId: uuid("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    claimedAt: timestamp("claimed_at", { mode: "date" }),
    /**
     * The claim link created or first linked the login, so later clicks (a
     * mail scanner may make the first) still route through the password page.
     */
    claimCreatedAccount: boolean("claim_created_account")
      .notNull()
      .default(false),
    status: assessmentLeadStatusEnum("status").notNull().default("new"),
    /** When the coach last changed the status; null while it is the default. */
    statusChangedAt: timestamp("status_changed_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("assessment_leads_coach_email_unique").on(
      table.coachId,
      table.email,
    ),
    index("assessment_leads_email_idx").on(table.email),
  ],
);

/**
 * One start of an assessment by a lead. Each start is a new act of consent,
 * so the consent record lives here. Reached only through two hashed
 * credentials: the browser's httpOnly cookie token (to complete it) and the
 * emailed claim token (to save the results to an account).
 */
export const assessmentLeadAttempts = pgTable(
  "assessment_lead_attempts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    leadId: uuid("lead_id")
      .notNull()
      .references(() => assessmentLeads.id, { onDelete: "cascade" }),
    template: coachAssessmentTemplateEnum("template").notNull(),
    wheelSessionId: text("wheel_session_id").references(
      () => wheelOfLifeSessions.id,
      { onDelete: "set null" },
    ),
    consentTextVersion: integer("consent_text_version").notNull(),
    consentedAt: timestamp("consented_at", { mode: "date" }).notNull(),
    utm: jsonb("utm").$type<Record<string, string>>(),
    /** Only the referring page's origin and path; never its query string. */
    referrerOrigin: text("referrer_origin"),
    referrerPath: text("referrer_path"),
    /** SHA-256 of the cookie token of the browser that started the attempt. */
    accessTokenHash: text("access_token_hash").notNull().unique(),
    /** SHA-256 of the emailed "Save my results" token. */
    claimTokenHash: text("claim_token_hash").unique(),
    claimExpiresAt: timestamp("claim_expires_at", { mode: "date" }),
    /** Set once the provider accepts the results email. */
    emailSentAt: timestamp("email_sent_at", { mode: "date" }),
    /** Set once the provider accepts the coach's new-lead notice. */
    coachNoticeSentAt: timestamp("coach_notice_sent_at", { mode: "date" }),
    /** The visitor first pressed the results page's contact button. */
    contactClickedAt: timestamp("contact_clicked_at", { mode: "date" }),
    startedAt: timestamp("started_at", { mode: "date" }).notNull(),
    completedAt: timestamp("completed_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => [
    index("assessment_lead_attempts_lead_idx").on(table.leadId),
    index("assessment_lead_attempts_session_idx").on(table.wheelSessionId),
  ],
);

/**
 * A client's choice to show one of their Wheel sessions to the coach of one
 * engagement. Its own table, not a session column: one session can be shared
 * with a later coach, and revoking keeps the session. Revoking sets
 * `revokedAt`; sharing again clears it, so a pair has at most one row.
 */
export const wheelSessionShares = pgTable(
  "wheel_session_shares",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sessionId: text("session_id")
      .notNull()
      .references(() => wheelOfLifeSessions.id, { onDelete: "cascade" }),
    engagementId: uuid("engagement_id")
      .notNull()
      .references(() => engagements.id, { onDelete: "cascade" }),
    sharedAt: timestamp("shared_at", { mode: "date" }).defaultNow().notNull(),
    revokedAt: timestamp("revoked_at", { mode: "date" }),
  },
  (table) => [
    uniqueIndex("wheel_session_shares_session_engagement_unique").on(
      table.sessionId,
      table.engagementId,
    ),
    index("wheel_session_shares_engagement_idx").on(table.engagementId),
  ],
);

/** One server-owned Checkout attempt remains active until Stripe settles it. */
export const novaCheckoutAttempts = pgTable(
  "nova_checkout_attempts",
  {
    id: uuid().primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    fingerprint: text().notNull(),
    params: jsonb().$type<Stripe.Checkout.SessionCreateParams>().notNull(),
    stripeSessionId: text("stripe_session_id"),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    state: text()
      .$type<
        "pending" | "open" | "retiring" | "completed" | "expired" | "failed"
      >()
      .notNull()
      .default("pending"),
    active: boolean().notNull().default(true),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    uniqueIndex("nova_checkout_attempts_active_coach")
      .on(table.coachId)
      .where(sql`${table.active} = true`),
  ],
);

/**
 * The video library: every video a coach page or a course can show. A row is
 * a link to the hosted video (Wistia, YouTube or Vimeo) plus the details the
 * player and the admin list show. Placements and course weeks point here, so
 * one recording can serve several spots.
 */
export const videos = pgTable("videos", {
  id: uuid().primaryKey().defaultRandom(),
  title: text().notNull(),
  url: text().notNull(),
  durationSeconds: integer("duration_seconds"),
  thumbnailUrl: text("thumbnail_url"),
  ...timestamps,
});

export type VideoSelect = typeof videos.$inferSelect;

/**
 * Which video a placement shows to which audience. A placement is a spot on a
 * coach page, named in `VIDEO_PLACEMENTS` (`~/lib/shared/learning/placements`);
 * the key is plain text so retiring a spot needs no migration, and reads skip
 * keys the registry no longer knows. No row for a coach's audiences means the
 * page shows no video there.
 */
export const videoPlacements = pgTable(
  "video_placements",
  {
    placement: text().notNull(),
    audience: text().$type<VideoAudience>().notNull(),
    videoId: uuid("video_id")
      .notNull()
      .references(() => videos.id, { onDelete: "restrict" }),
    ...timestamps,
  },
  (table) => [primaryKey({ columns: [table.placement, table.audience] })],
);

/**
 * A course coaches follow week by week — the Business Builder Course, and any
 * later one. A second version of a course is a new row with its own weeks and
 * its own progress. Drafts are visible to admins only.
 */
export const courses = pgTable("courses", {
  id: uuid().primaryKey().defaultRandom(),
  slug: text().notNull().unique(),
  /** The menu label and the name admins use: "Business Builder Course". */
  title: text().notNull(),
  /** The page headline: "Build your Client Acquisition System". */
  headline: text().notNull(),
  /** The lead line under the headline. */
  description: text().notNull().default(""),
  status: text().$type<CourseStatus>().notNull().default("draft"),
  ...timestamps,
});

/**
 * One week of a course: a class video (optional — later weeks are templates
 * and practice), what the week is about, and what it produces. `position` is
 * 1-based and is the week number coaches see.
 */
export const courseWeeks = pgTable(
  "course_weeks",
  {
    id: uuid().primaryKey().defaultRandom(),
    courseId: uuid("course_id")
      .notNull()
      .references(() => courses.id, { onDelete: "cascade" }),
    position: integer().notNull(),
    title: text().notNull(),
    summary: text().notNull().default(""),
    /** "What this week produces", in the coach's terms. */
    outcome: text().notNull().default(""),
    classVideoId: uuid("class_video_id").references(() => videos.id, {
      onDelete: "restrict",
    }),
    /**
     * The NOVA assets this week teaches her to build. Approving all of them
     * completes the week; a week with none is completed by hand.
     */
    producesAssets: text("produces_assets")
      .array()
      .$type<AssetKey[]>()
      .notNull()
      .default(sql`'{}'::text[]`),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("course_weeks_course_position").on(
      table.courseId,
      table.position,
    ),
  ],
);

/**
 * What a week hands out beside its class. Slides and templates are links; an
 * office-hours replay is a library video.
 */
export const courseMaterials = pgTable(
  "course_materials",
  {
    id: uuid().primaryKey().defaultRandom(),
    weekId: uuid("week_id")
      .notNull()
      .references(() => courseWeeks.id, { onDelete: "cascade" }),
    position: integer().notNull(),
    kind: text().$type<CourseMaterialKind>().notNull(),
    title: text().notNull(),
    /** The line under the title; empty uses the kind's default. */
    note: text(),
    url: text(),
    videoId: uuid("video_id").references(() => videos.id, {
      onDelete: "restrict",
    }),
    ...timestamps,
  },
  (table) => [
    index("course_materials_week").on(table.weekId, table.position),
    check(
      "course_materials_target",
      sql`(${table.kind} = 'replay' AND ${table.videoId} IS NOT NULL AND ${table.url} IS NULL) OR (${table.kind} <> 'replay' AND ${table.url} IS NOT NULL AND ${table.videoId} IS NULL)`,
    ),
  ],
);

/** A coach marked a week complete. Completing a week opens the next one. */
export const courseWeekCompletions = pgTable(
  "course_week_completions",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    weekId: uuid("week_id")
      .notNull()
      .references(() => courseWeeks.id, { onDelete: "cascade" }),
    completedAt: timestamp("completed_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.coachId, table.weekId] })],
);

/**
 * A coach's optional-email choices. This row is the source of truth; the
 * GoHighLevel `mail/*` category tags on her contact are its copy, and
 * GoHighLevel sends the mail. See `src/lib/server/coach-email-preferences.ts`.
 *
 * Every change to what GoHighLevel should hold bumps `sync_generation`. A
 * push records the generation it sent in `ghl_synced_generation`, so the row
 * is waiting for a push while the second is behind the first.
 */
export const coachEmailPreferences = pgTable("coach_email_preferences", {
  coachId: uuid("coach_id")
    .primaryKey()
    .references(() => coaches.id, { onDelete: "cascade" }),
  callReminders: boolean("call_reminders").notNull().default(true),
  productUpdates: boolean("product_updates").notNull().default(true),
  businessTips: boolean("business_tips").notNull().default(true),
  communityNews: boolean("community_news").notNull().default(true),
  // The weekly NOVA check-in email. The app sends it, so it has no
  // GoHighLevel tag and changing it never bumps `sync_generation`.
  weeklyCheckIn: boolean("weekly_check_in").notNull().default(true),
  // Set while she pauses all optional email. The sweep clears it once it has
  // passed, and only then do her category tags come back.
  pausedUntil: timestamp("paused_until", { mode: "date" }),
  // When she last changed a category or the pause herself.
  changedAt: timestamp("changed_at", { mode: "date" }).notNull().defaultNow(),
  syncGeneration: integer("sync_generation").notNull().default(1),
  ghlSyncedGeneration: integer("ghl_synced_generation").notNull().default(0),
  ghlSyncError: text("ghl_sync_error"),
  // First failure of the current failing streak; cleared by a push that works.
  ghlSyncFailingSince: timestamp("ghl_sync_failing_since", { mode: "date" }),
  // Set once the streak passes the give-up age: the sweep stops retrying
  // until her next change.
  ghlSyncGaveUpAt: timestamp("ghl_sync_gave_up_at", { mode: "date" }),
  ...timestamps,
});

export type CoachEmailPreferencesSelect =
  typeof coachEmailPreferences.$inferSelect;

// ---------------------------------------------------------------------------
// Upgrade prompts
// ---------------------------------------------------------------------------

/**
 * A coach closed an upgrade prompt (`UPGRADE_PROMPT_KEYS` in
 * `src/lib/shared/upgrade-prompts.ts`). One row per coach and prompt: a closed
 * prompt stays closed on every device, and the newest close starts the quiet
 * day in which no other prompt shows.
 */
export const upgradePromptDismissals = pgTable(
  "upgrade_prompt_dismissals",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    prompt: text("prompt").$type<UpgradePromptKey>().notNull(),
    dismissedAt: timestamp("dismissed_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.coachId, table.prompt] })],
);

// ---------------------------------------------------------------------------
// NOVA trial emails
// ---------------------------------------------------------------------------

/**
 * One row per trial subscription and scheduled trial email
 * (`NOVA_TRIAL_EMAIL_KEYS` in `src/lib/server/nova/trial-emails.ts`). The
 * sweep writes the row before it sends, so each email goes out at most once
 * per trial; a send that fails after its claim is a missed email, not a
 * second one.
 */
export const novaTrialEmailClaims = pgTable(
  "nova_trial_email_claims",
  {
    subscriptionId: text("subscription_id").notNull(),
    emailKey: text("email_key").notNull(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    claimedAt: timestamp("claimed_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.subscriptionId, table.emailKey] })],
);

// ---------------------------------------------------------------------------
// Features and their votes
// ---------------------------------------------------------------------------

/**
 * One row per coach-facing feature, from What's coming to live. `key` is the
 * primary key, is stored in every vote and never changes; a feature is never
 * deleted, it moves to `dropped`. `module`, `whats_coming_group` and `flag_key`
 * are checked on write against the lists in code. `flag_key` has no foreign key,
 * because a flag row appears only when the flag is first toggled. The row's
 * `created_at` keeps What's coming in page order. "Done" is not a stage here:
 * `src/lib/shared/features/stage.ts` reads it from `new_until`.
 */
export const features = pgTable(
  "features",
  {
    key: text("key").primaryKey(),
    title: text("title").notNull(),
    summary: text("summary").notNull(),
    module: text("module").$type<FeatureModule>().notNull(),
    whatsComingGroup: text("whats_coming_group").$type<WhatsComingGroup>(),
    /** Where a Coming soon card shows the feature, as the coach reads it. */
    cardLocation: text("card_location"),
    stage: text("stage")
      .$type<FeatureStoredStage>()
      .notNull()
      .default("planned"),
    stageEnteredAt: timestamp("stage_entered_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    newUntil: timestamp("new_until", { withTimezone: true }),
    flagKey: text("flag_key"),
    /** The page a live feature opens, for its notice and its update. */
    appPath: text("app_path"),
    ...timestamps,
  },
  (table) => [
    check(
      "features_stage_known",
      sql`${table.stage} in ('planned', 'beta', 'new', 'dropped')`,
    ),
    check(
      "features_planned_has_group",
      sql`${table.stage} <> 'planned' or ${table.whatsComingGroup} is not null`,
    ),
    check(
      "features_live_has_app_path",
      sql`${table.stage} not in ('beta', 'new') or ${table.appPath} is not null`,
    ),
    check(
      "features_new_until_only_in_new",
      sql`(${table.stage} = 'new') = (${table.newUntil} is not null)`,
    ),
  ],
);

export type FeatureSelect = typeof features.$inferSelect;

/**
 * One row per person and feature: "this person wants this feature". Taking a
 * vote back deletes the row. Keyed by user, not coach, so any part of the app
 * can collect votes. `notified_at` is set when the "Now available" notice goes
 * out, so it goes out once.
 */
export const featureVotes = pgTable(
  "feature_votes",
  {
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    itemKey: text("item_key")
      .notNull()
      .references(() => features.key),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    notifiedAt: timestamp("notified_at", { withTimezone: true }),
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.itemKey] }),
    index("feature_votes_item_key_idx").on(table.itemKey),
  ],
);

/**
 * One update on What's new. `modules` holds keys from
 * `~/lib/shared/features/modules`, checked on write. `href` is a path inside
 * the app. `published_at` is null while the update is a draft, and only a
 * published update ever reaches a coach.
 */
export const whatsNewEntries = pgTable(
  "whats_new_entries",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    title: text("title").notNull(),
    body: text("body").notNull(),
    href: text("href"),
    modules: text("modules").array().$type<FeatureModule[]>().notNull(),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    ...timestamps,
  },
  (table) => [
    check(
      "whats_new_entries_modules_not_empty",
      sql`cardinality(${table.modules}) > 0`,
    ),
  ],
);

export type WhatsNewEntrySelect = typeof whatsNewEntries.$inferSelect;

// ---------------------------------------------------------------------------
// Calendar connections
// ---------------------------------------------------------------------------

export const calendarProviderEnum = pgEnum(
  "calendar_provider",
  enumToPgEnum(CalendarProviderName),
);

export const calendarConnectionStatusEnum = pgEnum(
  "calendar_connection_status",
  enumToPgEnum(CalendarConnectionStatus),
);

/**
 * A coach's connection to a calendar service through Composio. Composio holds
 * and refreshes the access; DCM keeps only the Composio connected-account id
 * and the coach's choices. Code keeps at most one `active` or
 * `needs_reconnect` row per user; a sign-in that has not come back is a
 * `pending` row, and an ended connection stays as `disconnected` so the
 * calendar events written through it can still name it.
 */
export const calendarConnections = pgTable(
  "calendar_connections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    provider: calendarProviderEnum("provider").notNull(),
    composioConnectedAccountId: text("composio_connected_account_id")
      .notNull()
      .unique(),
    /** The calendar account's address; null until the sign-in completes. */
    accountEmail: text("account_email"),
    status: calendarConnectionStatusEnum("status")
      .notNull()
      .default(CalendarConnectionStatus.PENDING),
    /** Where DCM adds its events; the main calendar until she chooses. */
    writeCalendarId: text("write_calendar_id"),
    /** The calendars that count as busy; the main calendar until she chooses. */
    busyCalendarIds: text("busy_calendar_ids")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    /** `google_meet`: new sessions without a link of her own get a Meet link. */
    defaultMeetingProvider: meetingProviderEnum("default_meeting_provider")
      .notNull()
      .default(MeetingProvider.CUSTOM),
    /** When DCM last asked the provider whether it still has access. */
    lastCheckedAt: timestamp("last_checked_at", { mode: "date" }),
    /** When the provider last refused access. */
    lastErrorAt: timestamp("last_error_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => [
    index("calendar_connections_user_id_status_idx").on(
      table.userId,
      table.status,
    ),
  ],
);

export type CalendarConnectionSelect = typeof calendarConnections.$inferSelect;

/**
 * Composio requests per UTC day and tool (a tool slug, or `accounts.*` for
 * connection requests). The free plan caps tool calls a month, so this is the
 * count DCM watches to move plans before the cap.
 */
export const composioDailyCalls = pgTable(
  "composio_daily_calls",
  {
    day: date("day", { mode: "string" }).notNull(),
    tool: text("tool").notNull(),
    calls: integer("calls").notNull().default(0),
  },
  (table) => [primaryKey({ columns: [table.day, table.tool] })],
);

export const calendarEventSubjectTypeEnum = pgEnum(
  "calendar_event_subject_type",
  enumToPgEnum(CalendarEventSubjectType),
);

export const calendarEventSyncStateEnum = pgEnum(
  "calendar_event_sync_state",
  enumToPgEnum(CalendarEventSyncState),
);

/**
 * The event DCM keeps in a coach's calendar for one session or booked call.
 * DCM's own record stays the source of truth: the sync makes the event match
 * it, and the 15-minute cron retries rows left `pending`. `subjectId` has no
 * foreign key because it names a session or a booking; a row whose subject is
 * gone waits for its event to be removed, then goes too.
 */
export const calendarEvents = pgTable(
  "calendar_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    connectionId: uuid("connection_id")
      .notNull()
      .references(() => calendarConnections.id, { onDelete: "cascade" }),
    subjectType: calendarEventSubjectTypeEnum("subject_type").notNull(),
    subjectId: uuid("subject_id").notNull(),
    /** Null until the first write succeeds. */
    providerEventId: text("provider_event_id"),
    providerCalendarId: text("provider_calendar_id").notNull(),
    /** The client or booker is a guest, so the provider emails them. */
    guestInvited: boolean("guest_invited").notNull().default(false),
    syncState: calendarEventSyncStateEnum("sync_state")
      .notNull()
      .default(CalendarEventSyncState.PENDING),
    attempts: integer("attempts").notNull().default(0),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("calendar_events_subject_idx").on(
      table.subjectType,
      table.subjectId,
    ),
    index("calendar_events_sync_state_idx").on(table.syncState),
  ],
);

export type CalendarEventSelect = typeof calendarEvents.$inferSelect;
